import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../utils/axiosConfig";
import { generateMTCPdf } from "../utils/generateMTC";
import "./GateValveMTCForm.css";

// Format date to DD/MM/YYYY for display
function formatDateDDMMYYYY(dateValue) {
  if (!dateValue) return "";
  try {
    const str = String(dateValue).trim();
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return String(dateValue);
  }
}

// MTC Form Fields - These are the fields needed in the MTC/ITC certificate
// Static/Fixed text values
const staticValues = {
  test_result: [
    { value: "PASS - NO VISIBLE LEAKAGE FOUND", label: "PASS - NO VISIBLE LEAKAGE FOUND" },
    { value: "PASS - FOUND WITHIN ALLOWABLE LEAKAGE", label: "PASS - FOUND WITHIN ALLOWABLE LEAKAGE" },
    { value: "PASS", label: "PASS" },
    { value: "FAIL", label: "FAIL" },
  ],
  conformity_statement: [
    { value: "THE VALVE IS SUPPLIED IN ACCORDANCE WITH THE PURCHASE ORDER REQUIREMENTS AND FOUND SATISFACTORY.", label: "THE VALVE IS SUPPLIED IN ACCORDANCE WITH THE PURCHASE ORDER REQUIREMENTS AND FOUND SATISFACTORY." },
  ],
  testing_duration: [
    { value: "15 MINUTES", label: "15 MINUTES" },
    { value: "10 MINUTES", label: "10 MINUTES" },
    { value: "20 MINUTES", label: "20 MINUTES" },
    { value: "30 MINUTES", label: "30 MINUTES" },
  ],
  // LSB Static remarks
  static_remark: [
    { value: "LSB TEST CERTIFICATE ACCORDING TO EN 10204 TYPE 3.1", label: "LSB TEST CERTIFICATE ACCORDING TO EN 10204 TYPE 3.1" },
    { value: "CERTIFICATE AS PER EN 10204 3.1", label: "CERTIFICATE AS PER EN 10204 3.1" },
    { value: "PESO CERTIFICATION No.:", label: "PESO CERTIFICATION No.:" },
  ],
  // Ball valve Static values
  test_results: [
    { value: "NO VISIBLE LEAKAGE OBSERVED", label: "NO VISIBLE LEAKAGE OBSERVED" },
    { value: "SEAT LEAKAGE RATE: NO LEAKAGE", label: "SEAT LEAKAGE RATE: NO LEAKAGE" },
    { value: "PASS", label: "PASS" },
    { value: "FAIL", label: "FAIL" },
  ],
  inspection_statement: [
    { value: "THIS IS TO CERTIFY THAT THE ABOVE MATERIAL HAS BEEN TESTED AS PER RELEVANT STANDARD AND FOUND SATISFACTORY.", label: "THIS IS TO CERTIFY THAT THE ABOVE MATERIAL HAS BEEN TESTED AS PER RELEVANT STANDARD AND FOUND SATISFACTORY." },
  ],
  standard_remark: [
    { value: "1. ALL DIMENSIONS ARE AS PER APPROVED DRAWING.", label: "1. ALL DIMENSIONS ARE AS PER APPROVED DRAWING." },
    { value: "2. HYDRO TEST CARRIED OUT AT ABOVE MENTIONED PRESSURE FOR SPECIFIED DURATION.", label: "2. HYDRO TEST CARRIED OUT AT ABOVE MENTIONED PRESSURE FOR SPECIFIED DURATION." },
    { value: "3. MATERIAL TEST CERTIFICATES ARE ENCLOSED.", label: "3. MATERIAL TEST CERTIFICATES ARE ENCLOSED." },
    { value: "4. VALVE IS SUPPLIED AS PER PO REQUIREMENTS.", label: "4. VALVE IS SUPPLIED AS PER PO REQUIREMENTS." },
  ],
};

// Dropdown options from master data
const masterDataOptions = {
  design_standard: [
    { value: "API 600", label: "API 600" },
    { value: "API 600 14th Ed.2021", label: "API 600 14th Ed.2021" },
    { value: "API 600 15th Ed.", label: "API 600 15th Ed." },
    { value: "BS 1418", label: "BS 1418" },
    { value: "BS 5163", label: "BS 5163" },
    { value: "ISO 14313", label: "ISO 14313" },
    { value: "ISO 17292", label: "ISO 17292" },
    { value: "ASME B16.34", label: "ASME B16.34" },
  ],
  testing_std: [
    { value: "API 598", label: "API 598" },
    { value: "API 598 10th Ed.", label: "API 598 10th Ed." },
    { value: "ISO 14313", label: "ISO 14313" },
    { value: "BS EN 12266-1", label: "BS EN 12266-1" },
  ],
  end_connection: [
    { value: "Flanged EN 1092-1", label: "Flanged EN 1092-1" },
    { value: "Flanged ASME B16.5", label: "Flanged ASME B16.5" },
    { value: "Butt Weld", label: "Butt Weld" },
    { value: "Socket Weld", label: "Socket Weld" },
    { value: "Threaded NPT", label: "Threaded NPT" },
    { value: "Wafer Type", label: "Wafer Type" },
  ],
  std_edition: [
    { value: "ASME B16.5 2020", label: "ASME B16.5 2020" },
    { value: "ASME B16.47 Series A", label: "ASME B16.47 Series A" },
    { value: "ASME B16.47 Series B", label: "ASME B16.47 Series B" },
    { value: "EN 1092-1 2018", label: "EN 1092-1 2018" },
    { value: "MSS SP-44", label: "MSS SP-44" },
  ],
  raised_face_std: [
    { value: "ASME B16.5 RF", label: "ASME B16.5 RF" },
    { value: "EN 1092-1 Type A", label: "EN 1092-1 Type A" },
    { value: "JIS B2220", label: "JIS B2220" },
  ],
  class: [
    { value: "150", label: "150" },
    { value: "300", label: "300" },
    { value: "400", label: "400" },
    { value: "600", label: "600" },
    { value: "900", label: "900" },
    { value: "1500", label: "1500" },
    { value: "2500", label: "2500" },
  ],
  size: [
    { value: "1/2\"", label: "1/2\"" },
    { value: "3/4\"", label: "3/4\"" },
    { value: "1\"", label: "1\"" },
    { value: "1-1/2\"", label: "1-1/2\"" },
    { value: "2\"", label: "2\"" },
    { value: "3\"", label: "3\"" },
    { value: "4\"", label: "4\"" },
    { value: "6\"", label: "6\"" },
    { value: "8\"", label: "8\"" },
    { value: "10\"", label: "10\"" },
    { value: "12\"", label: "12\"" },
    { value: "14\"", label: "14\"" },
    { value: "16\"", label: "16\"" },
    { value: "18\"", label: "18\"" },
    { value: "20\"", label: "20\"" },
    { value: "24\"", label: "24\"" },
    { value: "26\"", label: "26\"" },
    { value: "28\"", label: "28\"" },
    { value: "30\"", label: "30\"" },
    { value: "36\"", label: "36\"" },
    { value: "40\"", label: "40\"" },
    { value: "42\"", label: "42\"" },
    { value: "48\"", label: "48\"" },
  ],
  // These are fallback options - actual options will be fetched from API
  // Prepared By and Approved By will be populated from tcds_reviewers table (Section 21)
  prepared_by_name: [], // Populated dynamically from API
  approved_by: [], // Populated dynamically from API
  qac_officer: [], // Populated dynamically from API
  // Unit will be auto-populated from TCDS data (unit_id field)
  unit: [], // Populated dynamically from API
  test_result: staticValues.test_result,
  conformity_statement: staticValues.conformity_statement,
  testing_duration: staticValues.testing_duration,
  static_remark: staticValues.static_remark,
  // LSB dropdowns
  switch_type: [
    { value: "LIMIT SWITCH", label: "LIMIT SWITCH" },
    { value: "PROXIMITY SENSOR", label: "PROXIMITY SENSOR" },
    { value: "SOLENOID VALVE", label: "SOLENOID VALVE" },
    { value: "POSITIONER", label: "POSITIONER" },
    { value: "FLOW SWITCH", label: "FLOW SWITCH" },
  ],
  switch_make: [
    { value: "SHELEMAC", label: "SHELEMAC" },
    { value: "SENSOTECH", label: "SENSOTECH" },
    { value: "CROUZET", label: "CROUZET" },
    { value: "AECO", label: "AECO" },
    { value: "MARSON", label: "MARSON" },
    { value: "METROL", label: "METROL" },
  ],
  voltage: [
    { value: "24V DC", label: "24V DC" },
    { value: "24V AC", label: "24V AC" },
    { value: "110V AC", label: "110V AC" },
    { value: "220V AC", label: "220V AC" },
    { value: "230V AC", label: "230V AC" },
  ],
  protection: [
    { value: "IP65", label: "IP65" },
    { value: "IP66", label: "IP66" },
    { value: "IP67", label: "IP67" },
    { value: "IP68", label: "IP68" },
    { value: "EX-PROOF", label: "EX-PROOF" },
  ],
  certification: [
    { value: "EN 10204 3.1", label: "EN 10204 3.1" },
    { value: "EN 10204 3.2", label: "EN 10204 3.2" },
    { value: "ISO 10497", label: "ISO 10497" },
    { value: "PESO", label: "PESO" },
    { value: "CMRS", label: "CMRS" },
  ],
  // Ball valve dropdowns
  valve_testing_std: [
    { value: "API 598", label: "API 598" },
    { value: "API 598 10th Ed.", label: "API 598 10th Ed." },
    { value: "ISO 14313", label: "ISO 14313" },
    { value: "BS EN 12266-1", label: "BS EN 12266-1" },
    { value: "ASME B16.34", label: "ASME B16.34" },
  ],
  type_of_test: [
    { value: "HYDRO SHELL", label: "HYDRO SHELL" },
    { value: "HYDRO SEAT", label: "HYDRO SEAT" },
    { value: "PNEU. SEAT", label: "PNEU. SEAT" },
    { value: "HYDRO SHELL + SEAT", label: "HYDRO SHELL + SEAT" },
    { value: "PNEU. SHELL + SEAT", label: "PNEU. SHELL + SEAT" },
  ],
  test_results: staticValues.test_results,
  test_duration: [
    { value: "15 SEC", label: "15 SEC" },
    { value: "30 SEC", label: "30 SEC" },
    { value: "60 SEC", label: "60 SEC" },
    { value: "1 MIN", label: "1 MIN" },
    { value: "5 MIN", label: "5 MIN" },
  ],
  inspection_statement: staticValues.inspection_statement,
  standard_remark: staticValues.standard_remark,
};

// inputType: "tcds" = From TCDS, "system" = System Generated, "manual" = Manual Entry, "dropdown" = Dropdown, "static" = Fixed Text
// Each valve type has its own specific fields based on the reference images

const ALL_TCDS_TYPES = ["Automation/Standard", "API 600"];
const CHECK_VALVE_TCDS_TYPES = ["Automation/Standard", "API 6D"];
const STANDARD_ONLY_TCDS_TYPES = ["Automation/Standard"];

// Valve type specific configurations based on reference images
// Each valve type has its own complete set of fields
const valveTypeConfig = {
  // ══════════════════════════════════════════════════════════════════════════════
  // GATE VALVE - Image 1: Inspection & Test Certificate (API 600)
  // ══════════════════════════════════════════════════════════════════════════════
  gate: {
    title: "Gate Valve MTC",
    subtitle: "INSPECTION & TEST CERTIFICATE (API 600)",
    tcdsTypes: ALL_TCDS_TYPES,
    valveKeywords: ["gate"],
    // All fields for Gate Valve form (from Image 1)
    formFields: [
      // Row 1: Certificate No (Auto-generate on submit), Date
      { id: "certificate_no", label: "CERTIFICATE NO.", source: "certificate_no", type: "text", inputType: "system", section: "header" },
      { id: "date", label: "DATE", source: "date", type: "date", inputType: "system", section: "header" },
      // Row 2: Customer, Tag No
      { id: "customer", label: "CUSTOMER", source: "customer", type: "text", inputType: "tcds", section: "header" },
      { id: "tag_no", label: "TAG NO.", source: "tag_no", type: "text", inputType: "tcds", section: "header" },
      // Row 3: P.O. No & Date, QAP No
      { id: "po_no", label: "P.O. NO. & DATE", source: "po_no", type: "text", inputType: "tcds", section: "header" },
      { id: "qap_no", label: "QAP NO", source: "qap_no", type: "text", inputType: "manual", section: "header" },
      // Row 4: TCDS No & Date, End Conn Std & Ed
      { id: "tcds_no", label: "TCDS NO. & DATE", source: "tcds_no", type: "text", inputType: "tcds", section: "header", required: true },
      { id: "end_connection", label: "END CONN. STD. & ED.", source: "End Connection", type: "select", inputType: "dropdown", section: "header" },
      // Row 5: GAD No, Raised Face Std & Ed
      { id: "gad_no", label: "GAD NO.", source: "gad_no", type: "text", inputType: "manual", section: "header" },
      { id: "raised_face_std", label: "RAISED FACE STD. & ED.", source: "raised_face_std", type: "select", inputType: "dropdown", section: "header" },
      // Row 6: Item Description, Trim
      { id: "item_description", label: "ITEM DESCRIPTION", source: "type", type: "text", inputType: "tcds", section: "header" },
      { id: "trim", label: "TRIM", source: "Trim", type: "text", inputType: "tcds", section: "header" },
      // Row 7: Size, Class
      { id: "size", label: "SIZE", source: "Size", type: "text", inputType: "tcds", section: "header" },
      { id: "class", label: "CLASS", source: "Class", type: "text", inputType: "tcds", section: "header" },
      // Row 8: SR. No, QTY
      { id: "sr_no", label: "SR. NO.", source: "sr_no", type: "text", inputType: "manual", section: "header" },
      { id: "qty", label: "QTY.", source: "Qty", type: "text", inputType: "manual", section: "header" },
      // Applicable Standards (Dropdown from Master)
      { id: "design_standard", label: "DESIGN STANDARD and EDITION", source: "design_standard", type: "select", inputType: "dropdown", section: "standards" },
      { id: "testing_std", label: "TESTING STD. AND EDITION", source: "testing_std", type: "select", inputType: "dropdown", section: "standards" },
      // Testing Details (Auto Fill from system/calculation)
      { id: "test_pressure_back_seat", label: "TEST PRESSURE - BACK SEAT (Kg/cm²)", source: "test_pressure_back_seat", type: "text", inputType: "auto", section: "testing" },
      { id: "test_pressure_body", label: "TEST PRESSURE - BODY (Kg/cm²)", source: "test_pressure_body", type: "text", inputType: "auto", section: "testing" },
      { id: "test_pressure_seat", label: "TEST PRESSURE - SEAT (Kg/cm²)", source: "test_pressure_seat", type: "text", inputType: "auto", section: "testing" },
      { id: "test_pressure_pneumatic", label: "PNEUMATIC TESTING - SEAT (Kg/cm²)", source: "test_pressure_pneumatic", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_back_seat", label: "TESTING DURATION - BACK SEAT", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_body", label: "TESTING DURATION - BODY", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_seat", label: "TESTING DURATION - SEAT", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_pneumatic", label: "TESTING DURATION - PNEUMATIC", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_back_seat", label: "TEST RESULT - BACK SEAT", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_body", label: "TEST RESULT - BODY", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_seat", label: "TEST RESULT - SEAT", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_pneumatic", label: "TEST RESULT - PNEUMATIC", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      // Customer Item Code (Remarks section)
      { id: "customer_item_code", label: "CUSTOMER ITEM CODE", source: "customer_item_code", type: "text", inputType: "manual", section: "remarks" },
      // Statement of Conformity
      { id: "conformity_statement", label: "STATEMENT OF CONFORMITY", source: "conformity_statement", type: "textarea", inputType: "static", section: "conformity" },
      // Signatures (Dropdown from Master)
      { id: "prepared_by_name", label: "PREPARED BY - NAME", source: "prepared_by_name", type: "select", inputType: "dropdown", section: "signature" },
      { id: "approved_by", label: "APPROVED BY", source: "approved_by", type: "select", inputType: "dropdown", section: "signature" },
    ],
    // Material Table - Each item has Material Spec (TCDS) + HEAT NO + MTC NO (Manual)
    materialFields: [
      // BODY
      { id: "body_material", label: "BODY - Material Spec", source: "Body", type: "text", inputType: "tcds", materialItem: "BODY" },
      { id: "body_heat_no", label: "BODY - Heat No.", source: "body_heat_no", type: "text", inputType: "manual", materialItem: "BODY" },
      { id: "body_mtc_no", label: "BODY - MTC No.", source: "body_mtc_no", type: "text", inputType: "manual", materialItem: "BODY" },
      // BONNET
      { id: "bonnet_material", label: "BONNET - Material Spec", source: "Bonnet", type: "text", inputType: "tcds", materialItem: "BONNET" },
      { id: "bonnet_heat_no", label: "BONNET - Heat No.", source: "bonnet_heat_no", type: "text", inputType: "manual", materialItem: "BONNET" },
      { id: "bonnet_mtc_no", label: "BONNET - MTC No.", source: "bonnet_mtc_no", type: "text", inputType: "manual", materialItem: "BONNET" },
      // WEDGE
      { id: "wedge_material", label: "WEDGE - Material Spec", source: "Wedge", type: "text", inputType: "tcds", materialItem: "WEDGE" },
      { id: "wedge_heat_no", label: "WEDGE - Heat No.", source: "wedge_heat_no", type: "text", inputType: "manual", materialItem: "WEDGE" },
      { id: "wedge_mtc_no", label: "WEDGE - MTC No.", source: "wedge_mtc_no", type: "text", inputType: "manual", materialItem: "WEDGE" },
      // SEATRING
      { id: "seatring_material", label: "SEATRING - Material Spec", source: "Seat Ring", type: "text", inputType: "tcds", materialItem: "SEATRING" },
      { id: "seatring_heat_no", label: "SEATRING - Heat No.", source: "seatring_heat_no", type: "text", inputType: "manual", materialItem: "SEATRING" },
      { id: "seatring_mtc_no", label: "SEATRING - MTC No.", source: "seatring_mtc_no", type: "text", inputType: "manual", materialItem: "SEATRING" },
      // STEM
      { id: "stem_material", label: "STEM - Material Spec", source: "Stem", type: "text", inputType: "tcds", materialItem: "STEM" },
      { id: "stem_heat_no", label: "STEM - Heat No.", source: "stem_heat_no", type: "text", inputType: "manual", materialItem: "STEM" },
      { id: "stem_mtc_no", label: "STEM - MTC No.", source: "stem_mtc_no", type: "text", inputType: "manual", materialItem: "STEM" },
      // PACKING
      { id: "packing_material", label: "PACKING - Material Spec", source: "Packing", type: "text", inputType: "tcds", materialItem: "PACKING" },
      { id: "packing_heat_no", label: "PACKING - Heat No.", source: "packing_heat_no", type: "text", inputType: "manual", materialItem: "PACKING" },
      { id: "packing_mtc_no", label: "PACKING - MTC No.", source: "packing_mtc_no", type: "text", inputType: "manual", materialItem: "PACKING" },
      // GASKET
      { id: "gasket_material", label: "GASKET - Material Spec", source: "Gasket", type: "text", inputType: "tcds", materialItem: "GASKET" },
      { id: "gasket_heat_no", label: "GASKET - Heat No.", source: "gasket_heat_no", type: "text", inputType: "manual", materialItem: "GASKET" },
      { id: "gasket_mtc_no", label: "GASKET - MTC No.", source: "gasket_mtc_no", type: "text", inputType: "manual", materialItem: "GASKET" },
      // STUD & NUT
      { id: "stud_nut_material", label: "STUD & NUT - Material Spec", source: "Stud & Nut", type: "text", inputType: "tcds", materialItem: "STUD & NUT" },
      { id: "stud_nut_heat_no", label: "STUD & NUT - Heat No.", source: "stud_nut_heat_no", type: "text", inputType: "manual", materialItem: "STUD & NUT" },
      { id: "stud_nut_mtc_no", label: "STUD & NUT - MTC No.", source: "stud_nut_mtc_no", type: "text", inputType: "manual", materialItem: "STUD & NUT" },
    ],
    // No separate materialManualFields - they're now part of materialFields
    materialManualFields: []
  },

  // ══════════════════════════════════════════════════════════════════════════════
  // CHECK VALVE - Image 2: Inspection & Test Certificate (API 598)
  // ══════════════════════════════════════════════════════════════════════════════
  check: {
    title: "Check Valve MTC",
    subtitle: "INSPECTION & TEST CERTIFICATE (API 598)",
    tcdsTypes: CHECK_VALVE_TCDS_TYPES,
    valveKeywords: ["check", "swing check", "non return", "nrv", "lift check"],
    formFields: [
      // Header
      { id: "certificate_no", label: "CERTIFICATE NO.", source: "certificate_no", type: "text", inputType: "system", section: "header" },
      { id: "date", label: "DATE", source: "date", type: "date", inputType: "system", section: "header" },
      { id: "customer", label: "CUSTOMER", source: "customer", type: "text", inputType: "tcds", section: "header" },
      { id: "tag_no", label: "TAG NO.", source: "tag_no", type: "text", inputType: "tcds", section: "header" },
      { id: "po_no", label: "P.O. NO. & DATE", source: "po_no", type: "text", inputType: "tcds", section: "header" },
      { id: "qap_no", label: "QAP NO", source: "qap_no", type: "text", inputType: "manual", section: "header" },
      { id: "tcds_no", label: "TCDS NO. & DATE", source: "tcds_no", type: "text", inputType: "tcds", section: "header", required: true },
      { id: "end_connection", label: "END CONN. STD. & ED.", source: "End Connection", type: "select", inputType: "dropdown", section: "header" },
      { id: "gad_no", label: "GAD NO.", source: "gad_no", type: "text", inputType: "manual", section: "header" },
      { id: "raised_face_std", label: "RAISED FACE STD. & ED.", source: "raised_face_std", type: "select", inputType: "dropdown", section: "header" },
      { id: "item_description", label: "ITEM DESCRIPTION", source: "type", type: "text", inputType: "tcds", section: "header" },
      { id: "trim", label: "TRIM", source: "Trim", type: "text", inputType: "tcds", section: "header" },
      { id: "size", label: "SIZE", source: "Size", type: "text", inputType: "tcds", section: "header" },
      { id: "class", label: "CLASS", source: "Class", type: "text", inputType: "tcds", section: "header" },
      { id: "sr_no", label: "SR. NO.", source: "sr_no", type: "text", inputType: "manual", section: "header" },
      { id: "qty", label: "QTY.", source: "Qty", type: "text", inputType: "manual", section: "header" },
      // Standards
      { id: "design_standard", label: "DESIGN STANDARD and EDITION", source: "design_standard", type: "select", inputType: "dropdown", section: "standards" },
      { id: "testing_std", label: "TESTING STD. AND EDITION", source: "testing_std", type: "select", inputType: "dropdown", section: "standards" },
      // Testing (Check valve has NA for Back Seat and Pneumatic)
      { id: "test_pressure_body", label: "TEST PRESSURE - BODY (Kg/cm²)", source: "test_pressure_body", type: "text", inputType: "auto", section: "testing" },
      { id: "test_pressure_seat", label: "TEST PRESSURE - SEAT (Kg/cm²)", source: "test_pressure_seat", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_body", label: "TESTING DURATION - BODY", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_seat", label: "TESTING DURATION - SEAT", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_body", label: "TEST RESULT - BODY", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_seat", label: "TEST RESULT - SEAT", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      // Remarks
      { id: "customer_item_code", label: "CUSTOMER ITEM CODE", source: "customer_item_code", type: "text", inputType: "manual", section: "remarks" },
      { id: "conformity_statement", label: "STATEMENT OF CONFORMITY", source: "conformity_statement", type: "textarea", inputType: "static", section: "conformity" },
      // Signatures
      { id: "prepared_by_name", label: "PREPARED BY - NAME", source: "prepared_by_name", type: "select", inputType: "dropdown", section: "signature" },
      { id: "approved_by", label: "APPROVED BY", source: "approved_by", type: "select", inputType: "dropdown", section: "signature" },
    ],
    // Material Table for Check Valve (5 rows from Image 2) - with per-item Heat No and MTC No
    materialFields: [
      // BODY
      { id: "body_material", label: "BODY - Material Spec", source: "Body", type: "text", inputType: "tcds", materialItem: "BODY" },
      { id: "body_heat_no", label: "BODY - Heat No.", source: "body_heat_no", type: "text", inputType: "manual", materialItem: "BODY" },
      { id: "body_mtc_no", label: "BODY - MTC No.", source: "body_mtc_no", type: "text", inputType: "manual", materialItem: "BODY" },
      // COVER
      { id: "cover_material", label: "COVER - Material Spec", source: "Cover", type: "text", inputType: "tcds", materialItem: "COVER" },
      { id: "cover_heat_no", label: "COVER - Heat No.", source: "cover_heat_no", type: "text", inputType: "manual", materialItem: "COVER" },
      { id: "cover_mtc_no", label: "COVER - MTC No.", source: "cover_mtc_no", type: "text", inputType: "manual", materialItem: "COVER" },
      // PLUG
      { id: "plug_material", label: "PLUG - Material Spec", source: "Plug", type: "text", inputType: "tcds", materialItem: "PLUG" },
      { id: "plug_heat_no", label: "PLUG - Heat No.", source: "plug_heat_no", type: "text", inputType: "manual", materialItem: "PLUG" },
      { id: "plug_mtc_no", label: "PLUG - MTC No.", source: "plug_mtc_no", type: "text", inputType: "manual", materialItem: "PLUG" },
      // GASKET
      { id: "gasket_material", label: "GASKET - Material Spec", source: "Gasket", type: "text", inputType: "tcds", materialItem: "GASKET" },
      { id: "gasket_heat_no", label: "GASKET - Heat No.", source: "gasket_heat_no", type: "text", inputType: "manual", materialItem: "GASKET" },
      { id: "gasket_mtc_no", label: "GASKET - MTC No.", source: "gasket_mtc_no", type: "text", inputType: "manual", materialItem: "GASKET" },
      // HEX BOLT
      { id: "hex_bolt_material", label: "HEX BOLT - Material Spec", source: "Hex Bolt", type: "text", inputType: "tcds", materialItem: "HEX BOLT" },
      { id: "hex_bolt_heat_no", label: "HEX BOLT - Heat No.", source: "hex_bolt_heat_no", type: "text", inputType: "manual", materialItem: "HEX BOLT" },
      { id: "hex_bolt_mtc_no", label: "HEX BOLT - MTC No.", source: "hex_bolt_mtc_no", type: "text", inputType: "manual", materialItem: "HEX BOLT" },
    ],
    materialManualFields: []
  },

  // ══════════════════════════════════════════════════════════════════════════════
  // GLOBE VALVE - Image 3: Inspection & Test Certificate (ISO 15761)
  // ══════════════════════════════════════════════════════════════════════════════
  globe: {
    title: "Globe Valve MTC",
    subtitle: "INSPECTION & TEST CERTIFICATE (ISO 15761)",
    tcdsTypes: STANDARD_ONLY_TCDS_TYPES,
    valveKeywords: ["globe"],
    formFields: [
      // Header
      { id: "certificate_no", label: "CERTIFICATE NO.", source: "certificate_no", type: "text", inputType: "system", section: "header" },
      { id: "date", label: "DATE", source: "date", type: "date", inputType: "system", section: "header" },
      { id: "customer", label: "CUSTOMER", source: "customer", type: "text", inputType: "tcds", section: "header" },
      { id: "tag_no", label: "TAG NO.", source: "tag_no", type: "text", inputType: "tcds", section: "header" },
      { id: "po_no", label: "P.O. NO. & DATE", source: "po_no", type: "text", inputType: "tcds", section: "header" },
      { id: "qap_no", label: "QAP NO", source: "qap_no", type: "text", inputType: "manual", section: "header" },
      { id: "tcds_no", label: "TCDS NO. & DATE", source: "tcds_no", type: "text", inputType: "tcds", section: "header", required: true },
      { id: "end_connection", label: "END CONN. STD. & ED.", source: "End Connection", type: "select", inputType: "dropdown", section: "header" },
      { id: "gad_no", label: "GAD NO.", source: "gad_no", type: "text", inputType: "manual", section: "header" },
      { id: "raised_face_std", label: "RAISED FACE STD. & ED.", source: "raised_face_std", type: "select", inputType: "dropdown", section: "header" },
      { id: "item_description", label: "ITEM DESCRIPTION", source: "type", type: "text", inputType: "tcds", section: "header" },
      { id: "trim", label: "TRIM", source: "Trim", type: "text", inputType: "tcds", section: "header" },
      { id: "size", label: "SIZE", source: "Size", type: "text", inputType: "tcds", section: "header" },
      { id: "class", label: "CLASS", source: "Class", type: "text", inputType: "tcds", section: "header" },
      { id: "sr_no", label: "SR. NO.", source: "sr_no", type: "text", inputType: "manual", section: "header" },
      { id: "qty", label: "QTY.", source: "Qty", type: "text", inputType: "manual", section: "header" },
      // Standards
      { id: "design_standard", label: "DESIGN STANDARD and EDITION", source: "design_standard", type: "select", inputType: "dropdown", section: "standards" },
      { id: "testing_std", label: "TESTING STD. AND EDITION", source: "testing_std", type: "select", inputType: "dropdown", section: "standards" },
      // Testing
      { id: "test_pressure_back_seat", label: "TEST PRESSURE - BACK SEAT (Kg/cm²)", source: "test_pressure_back_seat", type: "text", inputType: "auto", section: "testing" },
      { id: "test_pressure_body", label: "TEST PRESSURE - BODY (Kg/cm²)", source: "test_pressure_body", type: "text", inputType: "auto", section: "testing" },
      { id: "test_pressure_seat", label: "TEST PRESSURE - SEAT (Kg/cm²)", source: "test_pressure_seat", type: "text", inputType: "auto", section: "testing" },
      { id: "test_pressure_pneumatic", label: "PNEUMATIC TESTING - SEAT (Kg/cm²)", source: "test_pressure_pneumatic", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_back_seat", label: "TESTING DURATION - BACK SEAT", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_body", label: "TESTING DURATION - BODY", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_seat", label: "TESTING DURATION - SEAT", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "testing_duration_pneumatic", label: "TESTING DURATION - PNEUMATIC", source: "testing_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_back_seat", label: "TEST RESULT - BACK SEAT", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_body", label: "TEST RESULT - BODY", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_seat", label: "TEST RESULT - SEAT", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      { id: "test_result_pneumatic", label: "TEST RESULT - PNEUMATIC", source: "test_result", type: "text", inputType: "auto", section: "testing" },
      // Conformity
      { id: "conformity_statement", label: "STATEMENT OF CONFORMITY", source: "conformity_statement", type: "textarea", inputType: "static", section: "conformity" },
      // Signatures
      { id: "prepared_by_name", label: "PREPARED BY - NAME", source: "prepared_by_name", type: "select", inputType: "dropdown", section: "signature" },
      { id: "approved_by", label: "APPROVED BY", source: "approved_by", type: "select", inputType: "dropdown", section: "signature" },
    ],
    // Material Table for Globe Valve (8 rows from Image 3) - with per-item Heat No and MTC No
    materialFields: [
      // BODY
      { id: "body_material", label: "BODY - Material Spec", source: "Body", type: "text", inputType: "tcds", materialItem: "BODY" },
      { id: "body_heat_no", label: "BODY - Heat No.", source: "body_heat_no", type: "text", inputType: "manual", materialItem: "BODY" },
      { id: "body_mtc_no", label: "BODY - MTC No.", source: "body_mtc_no", type: "text", inputType: "manual", materialItem: "BODY" },
      // BONNET
      { id: "bonnet_material", label: "BONNET - Material Spec", source: "Bonnet", type: "text", inputType: "tcds", materialItem: "BONNET" },
      { id: "bonnet_heat_no", label: "BONNET - Heat No.", source: "bonnet_heat_no", type: "text", inputType: "manual", materialItem: "BONNET" },
      { id: "bonnet_mtc_no", label: "BONNET - MTC No.", source: "bonnet_mtc_no", type: "text", inputType: "manual", materialItem: "BONNET" },
      // PLUG
      { id: "plug_material", label: "PLUG - Material Spec", source: "Plug", type: "text", inputType: "tcds", materialItem: "PLUG" },
      { id: "plug_heat_no", label: "PLUG - Heat No.", source: "plug_heat_no", type: "text", inputType: "manual", materialItem: "PLUG" },
      { id: "plug_mtc_no", label: "PLUG - MTC No.", source: "plug_mtc_no", type: "text", inputType: "manual", materialItem: "PLUG" },
      // SEAT RING (Integral)
      { id: "seat_ring_material", label: "SEAT RING (Integral) - Material Spec", source: "Seat Ring", type: "text", inputType: "tcds", materialItem: "SEAT RING (Integral)" },
      { id: "seat_ring_heat_no", label: "SEAT RING - Heat No.", source: "seat_ring_heat_no", type: "text", inputType: "manual", materialItem: "SEAT RING (Integral)" },
      { id: "seat_ring_mtc_no", label: "SEAT RING - MTC No.", source: "seat_ring_mtc_no", type: "text", inputType: "manual", materialItem: "SEAT RING (Integral)" },
      // STEM
      { id: "stem_material", label: "STEM - Material Spec", source: "Stem", type: "text", inputType: "tcds", materialItem: "STEM" },
      { id: "stem_heat_no", label: "STEM - Heat No.", source: "stem_heat_no", type: "text", inputType: "manual", materialItem: "STEM" },
      { id: "stem_mtc_no", label: "STEM - MTC No.", source: "stem_mtc_no", type: "text", inputType: "manual", materialItem: "STEM" },
      // GASKET
      { id: "gasket_material", label: "GASKET - Material Spec", source: "Gasket", type: "text", inputType: "tcds", materialItem: "GASKET" },
      { id: "gasket_heat_no", label: "GASKET - Heat No.", source: "gasket_heat_no", type: "text", inputType: "manual", materialItem: "GASKET" },
      { id: "gasket_mtc_no", label: "GASKET - MTC No.", source: "gasket_mtc_no", type: "text", inputType: "manual", materialItem: "GASKET" },
      // GLAND PACKING
      { id: "gland_packing_material", label: "GLAND PACKING - Material Spec", source: "Gland Packing", type: "text", inputType: "tcds", materialItem: "GLAND PACKING" },
      { id: "gland_packing_heat_no", label: "GLAND PACKING - Heat No.", source: "gland_packing_heat_no", type: "text", inputType: "manual", materialItem: "GLAND PACKING" },
      { id: "gland_packing_mtc_no", label: "GLAND PACKING - MTC No.", source: "gland_packing_mtc_no", type: "text", inputType: "manual", materialItem: "GLAND PACKING" },
      // HEX BOLT
      { id: "hex_bolt_material", label: "HEX BOLT - Material Spec", source: "Hex Bolt", type: "text", inputType: "tcds", materialItem: "HEX BOLT" },
      { id: "hex_bolt_heat_no", label: "HEX BOLT - Heat No.", source: "hex_bolt_heat_no", type: "text", inputType: "manual", materialItem: "HEX BOLT" },
      { id: "hex_bolt_mtc_no", label: "HEX BOLT - MTC No.", source: "hex_bolt_mtc_no", type: "text", inputType: "manual", materialItem: "HEX BOLT" },
    ],
    materialManualFields: []
  },

  // ══════════════════════════════════════════════════════════════════════════════
  // BALL VALVE - Image 4: Compliance Certificate (API 6D)
  // ══════════════════════════════════════════════════════════════════════════════
  ball: {
    title: "Ball Valve MTC",
    subtitle: "COMPLIANCE CERTIFICATE (API 6D)",
    tcdsTypes: STANDARD_ONLY_TCDS_TYPES,
    valveKeywords: ["ball"],
    formFields: [
      // Header
      { id: "certificate_no", label: "CERTIFICATE NO", source: "certificate_no", type: "text", inputType: "system", section: "header" },
      { id: "date", label: "DATE", source: "date", type: "date", inputType: "system", section: "header" },
      { id: "customer", label: "CUSTOMER", source: "customer", type: "text", inputType: "tcds", section: "header" },
      // Valve Details
      { id: "valve_sr_no", label: "VALVE SR NO", source: "valve_sr_no", type: "text", inputType: "manual", section: "details" },
      { id: "batch_no", label: "BATCH NO", source: "batch_no", type: "text", inputType: "manual", section: "details" },
      { id: "description", label: "DESCRIPTION", source: "type", type: "text", inputType: "tcds", section: "details" },
      { id: "size", label: "SIZE", source: "Size", type: "text", inputType: "tcds", section: "details" },
      { id: "class", label: "CLASS", source: "Class", type: "text", inputType: "tcds", section: "details" },
      { id: "qty", label: "QTY", source: "Qty", type: "text", inputType: "manual", section: "details" },
      // Pressure Testing
      { id: "valve_testing_std", label: "VALVE TESTING STANDARD", source: "valve_testing_std", type: "select", inputType: "dropdown", section: "testing" },
      { id: "hydro_shell_pressure", label: "HYDRO SHELL - MIN PRESSURE", source: "hydro_shell_pressure", type: "text", inputType: "auto", section: "testing" },
      { id: "hydro_shell_duration", label: "HYDRO SHELL - MIN DURATION", source: "hydro_shell_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "hydro_seat_pressure", label: "HYDRO SEAT - MIN PRESSURE", source: "hydro_seat_pressure", type: "text", inputType: "auto", section: "testing" },
      { id: "hydro_seat_duration", label: "HYDRO SEAT - MIN DURATION", source: "hydro_seat_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "pneu_seat_pressure", label: "PNEU. SEAT - MIN PRESSURE", source: "pneu_seat_pressure", type: "text", inputType: "auto", section: "testing" },
      { id: "pneu_seat_duration", label: "PNEU. SEAT - MIN DURATION", source: "pneu_seat_duration", type: "text", inputType: "auto", section: "testing" },
      { id: "test_results", label: "TEST RESULTS", source: "test_results", type: "select", inputType: "static", section: "testing" },
      // Remark
      { id: "remark", label: "REMARK", source: "remark", type: "textarea", inputType: "manual", section: "remark" },
      // Signatures
      { id: "prepared_by_name", label: "PREPARED BY", source: "prepared_by_name", type: "select", inputType: "dropdown", section: "signature" },
      { id: "approved_by", label: "AUTHORISED BY", source: "approved_by", type: "select", inputType: "dropdown", section: "signature" },
    ],
    // Material of Construction (horizontal layout in PDF)
    materialFields: [
      { id: "body_ba", label: "BODY & B/A", source: "Body", type: "text", inputType: "tcds" },
      { id: "ball", label: "BALL", source: "Ball", type: "text", inputType: "tcds" },
      { id: "stem", label: "STEM", source: "Stem", type: "text", inputType: "tcds" },
      { id: "seat", label: "SEAT", source: "Seat", type: "text", inputType: "tcds" },
      { id: "seal", label: "SEAL", source: "Seal", type: "text", inputType: "tcds" },
      { id: "fasteners", label: "FASTNERS", source: "Fasteners", type: "text", inputType: "tcds" },
    ],
    materialManualFields: []
  },

  // ══════════════════════════════════════════════════════════════════════════════
  // ACTUATOR - Image 5: Actuator Test Certificate
  // ══════════════════════════════════════════════════════════════════════════════
  actuator: {
    title: "Actuator MTC",
    subtitle: "ACTUATOR TEST CERTIFICATE",
    tcdsTypes: STANDARD_ONLY_TCDS_TYPES,
    valveKeywords: ["actuator", "pneumatic actuator", "electric actuator"],
    formFields: [
      // Header
      { id: "project", label: "PROJECT", source: "project_name", type: "text", inputType: "tcds", section: "header" },
      { id: "date", label: "DATE", source: "date", type: "date", inputType: "system", section: "header" },
      { id: "customer", label: "CUSTOMER", source: "customer", type: "text", inputType: "tcds", section: "header" },
      { id: "tcds_no", label: "TCDS NO", source: "tcds_no", type: "text", inputType: "tcds", section: "header", required: true },
      { id: "job_order_no", label: "JOB OR. NO. DATE", source: "job_order_no", type: "text", inputType: "manual", section: "header" },
      { id: "rev_no", label: "REV NO & DATE", source: "rev_no", type: "text", inputType: "manual", section: "header" },
      // Product Details
      { id: "job_order_no_detail", label: "JOB ORDER NO.", source: "job_order_no", type: "text", inputType: "manual", section: "product" },
      { id: "actuator_model", label: "ACTUATOR MODEL", source: "Actuator Model", type: "text", inputType: "tcds", section: "product" },
      { id: "serial_batch_no", label: "SERIAL / BATCH NO.", source: "serial_batch_no", type: "text", inputType: "manual", section: "product" },
      { id: "qty", label: "QTY", source: "Qty", type: "text", inputType: "manual", section: "product" },
      // Test Results
      { id: "leak_test_pressure", label: "LEAK TEST PRESSURE", source: "leak_test_pressure", type: "text", inputType: "manual", section: "testing" },
      { id: "leak_test_remark", label: "LEAK TEST REMARK", source: "leak_test_remark", type: "text", inputType: "manual", section: "testing" },
      { id: "functional_test", label: "FUNCTIONAL TEST", source: "functional_test", type: "textarea", inputType: "manual", section: "testing" },
      { id: "visual_inspection", label: "VISUAL INSPECTION", source: "visual_inspection", type: "textarea", inputType: "manual", section: "testing" },
      { id: "declaration", label: "DECLARATION", source: "declaration", type: "textarea", inputType: "manual", section: "declaration" },
      // Signature
      { id: "prepared_by_name", label: "PREPARED BY", source: "prepared_by_name", type: "select", inputType: "dropdown", section: "signature" },
      { id: "qac_officer", label: "QAC OFFICER", source: "qac_officer", type: "select", inputType: "dropdown", section: "signature" },
    ],
    // Material Compliance
    materialFields: [
      { id: "body", label: "BODY", source: "Body", type: "text", inputType: "tcds" },
      { id: "piston", label: "PISTON", source: "Piston", type: "text", inputType: "tcds" },
      { id: "pinion", label: "PINION", source: "Pinion", type: "text", inputType: "tcds" },
      { id: "end_caps", label: "END CAPS", source: "End Caps", type: "text", inputType: "tcds" },
      { id: "pressure_rating", label: "PRESSURE RATING", source: "Pressure Rating", type: "text", inputType: "tcds" },
      { id: "fail_action_sr", label: "FAIL ACTION (S.R)", source: "Fail Action SR", type: "text", inputType: "tcds" },
      { id: "fail_action_da", label: "FAIL ACTION (D.A)", source: "Fail Action DA", type: "text", inputType: "tcds" },
    ],
    materialManualFields: []
  },

  // ══════════════════════════════════════════════════════════════════════════════
  // LSB - Image 6: LSB Test Certificate
  // ══════════════════════════════════════════════════════════════════════════════
  lsb: {
    title: "LSB MTC",
    subtitle: "LSB TEST CERTIFICATE",
    tcdsTypes: STANDARD_ONLY_TCDS_TYPES,
    valveKeywords: ["lsb", "limit switch box"],
    formFields: [
      // Header
      { id: "certificate_no", label: "CERTIFICATE NO.", source: "certificate_no", type: "text", inputType: "system", section: "header" },
      { id: "date", label: "DATE", source: "date", type: "date", inputType: "system", section: "header" },
      { id: "customer", label: "CUSTOMER", source: "customer", type: "text", inputType: "tcds", section: "header" },
      { id: "po_jo_no", label: "P.O. & J.O. NO. & DATE", source: "po_no", type: "text", inputType: "tcds", section: "header" },
      { id: "tcds_no", label: "TCDS NO. & DATE", source: "tcds_no", type: "text", inputType: "tcds", section: "header", required: true },
      { id: "qty", label: "QTY.", source: "Qty", type: "text", inputType: "manual", section: "header" },
      { id: "serial_no", label: "SERIAL NO.", source: "serial_no", type: "text", inputType: "manual", section: "header" },
      // Remark
      { id: "remark", label: "REMARK", source: "remark", type: "textarea", inputType: "manual", section: "remark" },
      // Signatures
      { id: "prepared_by_name", label: "PREPARED BY", source: "prepared_by_name", type: "select", inputType: "dropdown", section: "signature" },
      { id: "approved_by", label: "APPROVED BY", source: "approved_by", type: "select", inputType: "dropdown", section: "signature" },
    ],
    // LSB Details
    materialFields: [
      { id: "make", label: "MAKE", source: "Make", type: "text", inputType: "tcds" },
      { id: "enclosure", label: "ENCLOSURE", source: "Enclosure", type: "text", inputType: "tcds" },
      { id: "model", label: "MODEL", source: "Model", type: "text", inputType: "tcds" },
      { id: "switch_type", label: "SWITCH / SENSOR TYPE", source: "Switch Type", type: "select", inputType: "dropdown" },
      { id: "switch_model", label: "SWITCH / SENSOR MODEL", source: "Switch Model", type: "text", inputType: "manual" },
      { id: "switch_qty", label: "SWITCH / SENSOR QTY", source: "Switch Qty", type: "text", inputType: "manual" },
      { id: "switch_make", label: "SWITCH / SENSOR MAKE", source: "Switch Make", type: "select", inputType: "dropdown" },
      { id: "voltage", label: "VOLTAGE", source: "Voltage", type: "select", inputType: "dropdown" },
      { id: "cable_entry", label: "CABLE ENTRY & QTY", source: "Cable Entry", type: "text", inputType: "manual" },
      { id: "protection", label: "PROTECTION", source: "Protection", type: "select", inputType: "dropdown" },
      { id: "certification", label: "CERTIFICATION", source: "Certification", type: "text", inputType: "manual" },
    ],
    materialManualFields: []
  },

  // Automation (generic)
  automation: {
    title: "Automation MTC",
    subtitle: "Certificate of Compliance According to EN 10204 Type 3.1",
    tcdsTypes: STANDARD_ONLY_TCDS_TYPES,
    valveKeywords: ["automation", "positioner", "limit switch", "solenoid"],
    formFields: [
      // Basic Certificate Details
      { id: "certificate_no", label: "CERTIFICATE NO.", source: "certificate_no", type: "text", inputType: "system", section: "header" },
      { id: "date", label: "CERTI. DATE", source: "date", type: "date", inputType: "system", section: "header" },
      { id: "customer", label: "CUSTOMER NAME", source: "customer", type: "text", inputType: "tcds", section: "header" },
      { id: "project_name", label: "PROJECT NAME", source: "project_name", type: "text", inputType: "tcds", section: "header" },
      { id: "po_no", label: "CUSTOMER P.O. NO.", source: "po_no", type: "text", inputType: "tcds", section: "header" },
      { id: "po_date", label: "P. O. DATE", source: "po_date", type: "date", inputType: "tcds", section: "header" },
      { id: "oa_no", label: "O. A. NO.", source: "oa_no", type: "text", inputType: "tcds", section: "header" },
      { id: "oa_date", label: "O. A. DATE", source: "oa_date", type: "date", inputType: "manual", section: "header" },
      { id: "ga_drawing_no", label: "GA DRAWING NO.", source: "ga_drawing_no", type: "text", inputType: "manual", section: "header" },
      { id: "qap_no", label: "QAP NO.", source: "qap_no", type: "text", inputType: "manual", section: "header" },
      { id: "size", label: "SIZE", source: "Size", type: "text", inputType: "tcds", section: "header" },
      { id: "class", label: "CLASS", source: "Class", type: "text", inputType: "tcds", section: "header" },
      { id: "fail_position", label: "FAIL POSITION", source: "fail_position", type: "text", inputType: "manual", section: "header" },
      { id: "qty", label: "QTY. (NOS.)", source: "qty", type: "text", inputType: "tcds", section: "header" },
      { id: "power_fail_position", label: "POWER FAIL POSITION", source: "power_fail_position", type: "text", inputType: "manual", section: "header" },
      { id: "customer_item_code", label: "CUSTOMER ITEM CODE", source: "customer_item_code", type: "text", inputType: "tcds", section: "header" },

      // Valve Details (when valve is selected)
      { id: "ipc_sr_no", label: "IPC SR. NO.", source: "ipc_sr_no", type: "text", inputType: "tcds", section: "automation_valve" },
      { id: "valve_batch_sr_no", label: "VALVE BATCH/SR.NO.", source: "valve_batch_sr_no", type: "text", inputType: "tcds", section: "automation_valve" },
      { id: "tag_no", label: "TAG NO.", source: "tag_no", type: "text", inputType: "tcds", section: "automation_valve" },

      // Testing Pressure Details
      { id: "testing_standard", label: "TESTING STANDARD", source: "testing_standard", type: "select", inputType: "dropdown", section: "automation_valve" },
      { id: "min_air_supply", label: "MIN AIR SUPPLY TO ACTUATOR", source: "min_air_supply", type: "text", inputType: "tcds", section: "automation_valve" },
      { id: "pressure_rating", label: "PRESSURE RATING", source: "pressure_rating", type: "text", inputType: "linked", section: "automation_valve" },
      { id: "body_hydrostatic", label: "BODY HYDROSTATIC", source: "body_hydrostatic", type: "text", inputType: "linked", section: "automation_valve" },
      { id: "seat_hydrostatic", label: "SEAT HYDROSTATIC", source: "seat_hydrostatic", type: "text", inputType: "linked", section: "automation_valve" },
      { id: "seat_pneumatic", label: "SEAT PNEUMATIC", source: "seat_pneumatic", type: "text", inputType: "linked", section: "automation_valve" },
      { id: "test_duration", label: "TEST DURATION", source: "test_duration", type: "text", inputType: "linked", section: "automation_valve" },
      { id: "observation", label: "OBSERVATION", source: "observation", type: "textarea", inputType: "manual", section: "automation_valve" },

      // Remark
      { id: "remark", label: "REMARK", source: "remark", type: "textarea", inputType: "manual", section: "remark" },

      // Declaration
      { id: "declaration_of_compliance", label: "DECLARATION OF COMPLIANCE", source: "declaration_of_compliance", type: "select", inputType: "dropdown", section: "declaration" },

      // Signature/Approval
      { id: "prepared_by", label: "PREPARED BY", source: "prepared_by", type: "select", inputType: "dropdown", section: "signature" },
      { id: "approved_by", label: "APPROVED BY", source: "approved_by", type: "select", inputType: "dropdown", section: "signature" },
    ],
    materialFields: [],
    materialManualFields: []
  }
};

function ValveMTCForm({ valveType, onBack }) {
  const navigate = useNavigate();
  const { id: mtcId } = useParams();
  const editMode = !!mtcId;

  const config = valveTypeConfig[valveType] || valveTypeConfig.gate;
  // Use formFields + materialFields instead of mtcFormFields + itemDescriptionFields
  const allFields = [...(config.formFields || []), ...(config.materialFields || []), ...(config.materialManualFields || [])];
  
  const [view, setView] = useState("list"); // "list" | "form"
  const [tcdsList, setTcdsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingTCDS, setFetchingTCDS] = useState(false);
  const [selectedTCDS, setSelectedTCDS] = useState(null);
  const [selectedTCDSData, setSelectedTCDSData] = useState(null); // Full TCDS data for PDF generation
  const [hasValveInTCDS, setHasValveInTCDS] = useState(false); // Track if selected TCDS has a valve
  const [existingMtcId, setExistingMtcId] = useState(null); // Track existing MTC for update instead of create
  const [existingMtcCertNo, setExistingMtcCertNo] = useState(null); // Preserve existing certificate number
  const [notification, setNotification] = useState({ show: false, type: "", message: "" });
  const [isDownloading, setIsDownloading] = useState(false); // Separate state for PDF download
  const downloadLockRef = useRef(false); // Ref-based lock to prevent duplicate click calls
  
  // Dynamic dropdown options fetched from API
  const [unitOptions, setUnitOptions] = useState([]);
  const [reviewerOptions, setReviewerOptions] = useState([]);
  const [declarationOptions, setDeclarationOptions] = useState([]); // Declaration of Compliance options
  const [testingStandardOptions, setTestingStandardOptions] = useState([]); // Testing Standard options

  // Heat Annexure state
  const [attachHeatAnnexure, setAttachHeatAnnexure] = useState(false);
  const [heatAnnexureComponents, setHeatAnnexureComponents] = useState([]); // Available components from standard master
  const [selectedHeatComponents, setSelectedHeatComponents] = useState([]); // User selected components
  const [heatAnnexureQuantity, setHeatAnnexureQuantity] = useState(1);
  const [heatAnnexureData, setHeatAnnexureData] = useState([]); // Array of {valveNo, component, heatNumber, material, tcNo, vendorName}
  const [heatAnnexureMaster, setHeatAnnexureMaster] = useState([]); // Data from heat annexure master table

  const normalizeText = (value) => String(value || "").trim().toLowerCase();

  const extractJsonText = (value) => {
    try {
      if (!value) return "";
      const obj = typeof value === "string" ? JSON.parse(value) : value;
      return normalizeText(JSON.stringify(obj));
    } catch {
      return normalizeText(value);
    }
  };

  // Parse accessories_data to extract accessory types
  const getAccessoryTypes = (record) => {
    const accessories = [];
    
    try {
      // Check accessories_data field
      if (record.accessories_data) {
        const data = typeof record.accessories_data === "string" 
          ? JSON.parse(record.accessories_data) 
          : record.accessories_data;
        
        // accessories_data is usually an array of objects like [{accessoryName: "Actuator", accessoryId: 1, model: "...", parameters: {...}}, ...]
        if (Array.isArray(data)) {
          data.forEach(item => {
            // Check all possible field names for accessory name
            const accName = normalizeText(
              item.accessoryName ||  // Primary field used in TCDS form
              item.component || 
              item.type || 
              item.name || 
              item.accessory_type || 
              item.accessory_name ||
              ""
            );
            if (accName) accessories.push(accName);
          });
        } else if (typeof data === 'object') {
          // Handle object format
          Object.values(data).forEach(val => {
            if (typeof val === 'string') {
              const accName = normalizeText(val);
              if (accName) accessories.push(accName);
            }
          });
        }
      }
      
      // Also check dynamic_parameters for accessories
      if (record.dynamic_parameters) {
        const dynParams = typeof record.dynamic_parameters === "string"
          ? JSON.parse(record.dynamic_parameters)
          : record.dynamic_parameters;
        
        if (dynParams.accessories && Array.isArray(dynParams.accessories)) {
          dynParams.accessories.forEach(acc => {
            const accName = normalizeText(acc.accessoryName || acc.component || acc.type || acc.name || "");
            if (accName) accessories.push(accName);
          });
        }
        // Check for individual accessory fields
        if (dynParams.actuator || dynParams.has_actuator) {
          accessories.push("actuator");
        }
        if (dynParams.lsb || dynParams.has_lsb || dynParams.limit_switch_box) {
          accessories.push("lsb");
        }
      }
      
      // Check section_data for accessories
      if (record.section_data) {
        const sectionData = typeof record.section_data === "string"
          ? JSON.parse(record.section_data)
          : record.section_data;
        
        if (sectionData.accessories && Array.isArray(sectionData.accessories)) {
          sectionData.accessories.forEach(acc => {
            const accName = normalizeText(acc.accessoryName || acc.component || acc.type || acc.name || "");
            if (accName) accessories.push(accName);
          });
        }
      }
    } catch {
      // Ignore parse errors
    }
    
    // Debug logging for accessories
    if (process.env.NODE_ENV === 'development' && accessories.length > 0) {
      console.log(`[TCDS Filter] record.id=${record.id}, accessories found:`, accessories);
    }
    
    return [...new Set(accessories)]; // Return unique values
  };

  // Map valve subtype names to MTC categories
  // This handles valve types like "Wedge Gate" → "gate", "Trunnion Ball" → "ball", etc.
  const VALVE_SUBTYPE_MAPPING = {
    // Gate valve subtypes
    "wedge gate": "gate",
    "knife gate": "gate",
    "parallel gate": "gate",
    "slab gate": "gate",
    "conduit gate": "gate",
    "gate valve": "gate",
    // Globe valve subtypes  
    "globe": "globe",
    "globe valve": "globe",
    "globe stop": "globe",
    "globe angle": "globe",
    "angle globe": "globe",
    "stop globe": "globe",
    "y-pattern globe": "globe",
    "y pattern globe": "globe",
    // Check valve subtypes
    "swing check": "check",
    "dual plate check": "check",
    "lift check": "check",
    "tilting disc check": "check",
    "piston check": "check",
    "check valve": "check",
    "nrv": "check",
    "non return": "check",
    // Ball valve subtypes
    "trunnion ball": "ball",
    "floating ball": "ball",
    "full bore ball": "ball",
    "reduced bore ball": "ball",
    "v-port ball": "ball",
    "3-way ball": "ball",
    "multiport ball": "ball",
    "ball valve": "ball",
    // Butterfly valve subtypes
    "centric": "butterfly",
    "eccentric": "butterfly",
    "double eccentric": "butterfly",
    "triple eccentric": "butterfly",
    "butterfly": "butterfly",
    "butterfly valve": "butterfly",
    // Plug valve subtypes
    "plug": "plug",
    "lubricated plug": "plug",
    "non-lubricated plug": "plug",
    "plug valve": "plug",
  };

  // Get MTC category from valve type string
  const getValveCategory = (typeStr) => {
    if (!typeStr) return null;
    const normalized = normalizeText(typeStr);
    
    // Check exact match first
    if (VALVE_SUBTYPE_MAPPING[normalized]) {
      return VALVE_SUBTYPE_MAPPING[normalized];
    }
    
    // Check partial match
    for (const [subtype, category] of Object.entries(VALVE_SUBTYPE_MAPPING)) {
      if (normalized.includes(subtype)) {
        return category;
      }
    }
    
    // Fallback to keyword matching
    if (normalized.includes("gate")) return "gate";
    if (normalized.includes("globe")) return "globe";
    if (normalized.includes("check") || normalized.includes("swing") || normalized.includes("nrv")) return "check";
    if (normalized.includes("ball")) return "ball";
    if (normalized.includes("butterfly") || normalized.includes("centric") || normalized.includes("eccentric")) return "butterfly";
    if (normalized.includes("plug")) return "plug";
    
    return null;
  };

  // Check if record has a valve type selected and get its category
  const hasValveSelected = (record) => {
    const recordType = normalizeText(record.type);
    
    // Also check dynamic_parameters and section_data
    let dynamicType = "";
    let sectionType = "";
    try {
      if (record.dynamic_parameters) {
        const dynParams = typeof record.dynamic_parameters === "string"
          ? JSON.parse(record.dynamic_parameters)
          : record.dynamic_parameters;
        dynamicType = normalizeText(dynParams.type || dynParams.valve_type || "");
      }
      if (record.section_data) {
        const sectionData = typeof record.section_data === "string"
          ? JSON.parse(record.section_data)
          : record.section_data;
        sectionType = normalizeText(sectionData.type || sectionData.valve_type || "");
      }
    } catch { /* ignore */ }
    
    // Check if any of the type fields map to a valve category
    return getValveCategory(recordType) !== null || 
           getValveCategory(dynamicType) !== null || 
           getValveCategory(sectionType) !== null;
  };

  // Get the actual valve category for a record
  const getRecordValveCategory = (record) => {
    const recordType = normalizeText(record.type);
    let dynamicType = "";
    let sectionType = "";
    
    try {
      if (record.dynamic_parameters) {
        const dynParams = typeof record.dynamic_parameters === "string"
          ? JSON.parse(record.dynamic_parameters)
          : record.dynamic_parameters;
        dynamicType = normalizeText(dynParams.type || dynParams.valve_type || "");
      }
      if (record.section_data) {
        const sectionData = typeof record.section_data === "string"
          ? JSON.parse(record.section_data)
          : record.section_data;
        sectionType = normalizeText(sectionData.type || sectionData.valve_type || "");
      }
    } catch { /* ignore */ }
    
    // Return first matching category
    return getValveCategory(recordType) || getValveCategory(dynamicType) || getValveCategory(sectionType) || null;
  };

  // Main filtering logic based on MTC type requirements
  const recordLooksLikeSelectedValve = (record) => {
    const recordType = normalizeText(record.type);
    const sectionNumber = normalizeText(record.section_number);
    const sectionTitle = normalizeText(record.section_title);
    const accessoryTypes = getAccessoryTypes(record);
    const tcdsType = normalizeText(record.tcds_type);
    
    // Get the valve category using our mapping
    const valveCategory = getRecordValveCategory(record);
    
    // Also check dynamic_parameters for valve type
    let dynamicType = "";
    try {
      if (record.dynamic_parameters) {
        const dynParams = typeof record.dynamic_parameters === "string" 
          ? JSON.parse(record.dynamic_parameters) 
          : record.dynamic_parameters;
        dynamicType = normalizeText(dynParams.type || dynParams.valve_type || "");
      }
    } catch { /* ignore */ }
    
    // Also check section_data for valve type
    let sectionDataType = "";
    try {
      if (record.section_data) {
        const sectionData = typeof record.section_data === "string"
          ? JSON.parse(record.section_data)
          : record.section_data;
        // Check various possible locations for type
        sectionDataType = normalizeText(
          sectionData.type || 
          sectionData.valve_type || 
          sectionData["Valve Type"] ||
          (sectionData.valve && sectionData.valve.type) ||
          ""
        );
      }
    } catch { /* ignore */ }
    
    // Helper to check if any identifying field contains the keyword
    const matchesKeyword = (keyword) => {
      return recordType.includes(keyword) || 
             sectionNumber.includes(keyword) || 
             sectionTitle.includes(keyword) ||
             dynamicType.includes(keyword) ||
             sectionDataType.includes(keyword);
    };
    
    // Helper to check if this is Automation/Standard type
    const isAutomationStandard = () => {
      return tcdsType === "automation/standard" || 
             tcdsType.includes("automation") || 
             tcdsType.includes("standard");
    };
    
    // Check if record has any valve selected
    const recordHasValve = hasValveSelected(record);
    
    // Debug logging
    console.log(`[TCDS Filter] valveType=${valveType}, id=${record.id}, tcdsType=${tcdsType}, recordType=${recordType}, valveCategory=${valveCategory}, hasValve=${recordHasValve}, accessories=${accessoryTypes.join(',')}`);
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // 1. GATE MTC: All API 600 + Standard/Automation with Gate valve
    // ═══════════════════════════════════════════════════════════════════════════════
    if (valveType === "gate") {
      // Include ALL API 600 records (Gate valve specific)
      if (tcdsType === "api 600") {
        return true;
      }
      // Include Automation/Standard where valve category is "gate"
      if (isAutomationStandard() && valveCategory === "gate") {
        return true;
      }
      return false;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // 2. GLOBE MTC: Only Standard/Automation with Globe valve (with or without accessories)
    // ═══════════════════════════════════════════════════════════════════════════════
    if (valveType === "globe") {
      // Only Automation/Standard with Globe valve category
      if (isAutomationStandard() && valveCategory === "globe") {
        return true;
      }
      return false;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // 3. CHECK MTC: All API 6D + Standard/Automation with Check valve
    // ═══════════════════════════════════════════════════════════════════════════════
    if (valveType === "check") {
      // Include ALL API 6D records (Check valve specific)
      if (tcdsType === "api 6d") {
        return true;
      }
      // Include Automation/Standard where valve category is "check"
      if (isAutomationStandard() && valveCategory === "check") {
        return true;
      }
      return false;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // 4. BALL MTC: Only Standard/Automation with Ball valve (with or without accessories)
    // ═══════════════════════════════════════════════════════════════════════════════
    if (valveType === "ball") {
      // Only Automation/Standard with Ball valve category
      if (isAutomationStandard() && valveCategory === "ball") {
        return true;
      }
      return false;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // 5. LSB MTC: Only LSB accessory, NO valve
    // ═══════════════════════════════════════════════════════════════════════════════
    if (valveType === "lsb") {
      if (isAutomationStandard()) {
        // Must have NO valve selected
        if (recordHasValve) return false;
        
        // Must have LSB accessory
        const hasLSB = accessoryTypes.some(acc => 
          acc.includes("lsb") || acc.includes("limit switch box") || acc.includes("limit switch")
        );
        if (hasLSB) return true;
      }
      return false;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // 6. ACTUATOR MTC: Only Actuator accessory, valve
    // ═══════════════════════════════════════════════════════════════════════════════
    if (valveType === "actuator") {
      if (isAutomationStandard()) {
        // Must have NO valve selected
        if (recordHasValve) return false
        
        // Must have Actuator accessory
        const hasActuator = accessoryTypes.some(acc => 
          acc.includes("actuator") || acc.includes("pneumatic actuator") || 
          acc.includes("electric actuator")
        );
        if (hasActuator) return true;
      }
      return false;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // 7. AUTOMATION MTC: More than one accessories with different types, NO valve
    // ═══════════════════════════════════════════════════════════════════════════════
    if (valveType === "automation") {
      if (isAutomationStandard()) {
        // Must have NO valve selected
        if (recordHasValve) return true
        
        // Must have more than 1 accessory type
        if (accessoryTypes.length > 1) {
          return true;
        }
      }
      return false;
    }
    
    // Default: no match
    return false;
  };
  
  // MTC Form Data
  const [formData, setFormData] = useState(() => {
    const initialData = {};
    allFields.forEach(field => {
      initialData[field.id] = "";
    });
    return initialData;
  });
  
  // Tag numbers state for dynamic tag number fields
  const [tagNumbers, setTagNumbers] = useState([]);

  // Arrays for dynamic Valve Details rows (Automation/Standard MTC only)
  const [ipcSrNoArray, setIpcSrNoArray] = useState([]);
  const [valveBatchSrNoArray, setValveBatchSrNoArray] = useState([]);
  const [tagNumbersFromTCDS, setTagNumbersFromTCDS] = useState([]);

  // Load existing MTC data for edit mode
  const loadExistingMTC = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`/mtc/${id}`);
      if (response.data && response.data.success && response.data.data) {
        const mtcData = response.data.data;
        console.log("[DEBUG] Full MTC Data from API:", mtcData);
        console.log("[DEBUG] Remarks field value:", mtcData.remarks);

        // Set existing MTC ID for later use in update
        setExistingMtcId(mtcData.id);
        setExistingMtcCertNo(mtcData.certificate_number);

        // Extract TCDS data for form population
        const tcdsData = {
          id: mtcData.tcds_id,
          tcds_no: mtcData.tcds_no,
          tcds_date: mtcData.tcds_date,
          valve_type_id: mtcData.tcds_id, // Use tcds_id as reference
        };

        setSelectedTCDS(tcdsData);

        // Build initial form data from MTC
        const newFormData = {};
        allFields.forEach(field => {
          newFormData[field.id] = "";
        });

        // Populate form fields from MTC data
        // Direct field mappings
        const directMappings = {
          certificate_no: "certificate_number",
          date: "date",
          customer: "customer",
          tag_no: "tag_no",
          po_no: "po_no",
          po_date: "po_date",
          qap_no: "qap_no",
          tcds_no: "tcds_no",
          tcds_date: "tcds_date",
          end_connection: "end_connection",
          raised_face_std: "raised_face_std",
          gad_no: "gad_no",
          item_description: "item_description",
          trim: "trim",
          size: "size",
          class: "class",
          sr_no: "sr_no",
          qty: "qty",
          batch_no: "batch_no",
          design_standard: "design_standard",
          design_type: "design_standard",
          testing_std: "testing_standard", // Map database column to form field
          test_pressure: "test_pressure",
          testing_duration: "testing_duration",
          test_result: "test_result",
          test_pressure_back_seat: "hydro_back_seat",
          test_pressure_body: "hydro_body",
          test_pressure_seat: "hydro_seat",
          test_pressure_pneumatic: "pneumatic_seat",
          customer_item_code: "customer_item_code",
          conformity_statement: "conformity_statement",
          prepared_by_name: "prepared_by_name",
          approved_by: "approved_by",
          prepared_by: "prepared_by",
          qac_officer: "qac_officer",
          // Automation/Manual entry fields
          remark: "remarks", // Map database column 'remarks' to form field 'remark'
          oa_no: "oa_no",
          oa_date: "oa_date",
          ga_drawing_no: "ga_drawing_no",
          fail_position: "fail_position",
          power_fail_position: "power_fail_position",
          ipc_sr_no: "ipc_sr_no",
          valve_batch_sr_no: "valve_batch_sr_no",
          observation: "observation",
          min_air_supply: "min_air_supply",
          pressure_rating: "pressure_rating",
          declaration_of_compliance: "declaration_of_compliance",
          // Material fields
          body_material: "body_material",
          disc_material: "disc_material",
          stem_material: "stem_material",
          seat_material: "seat_material",
          bonnet_material: "bonnet_material",
          gasket_material: "gasket_material",
          packing_material: "packing_material",
          bolting_material: "bolting_material",
        };

        Object.entries(directMappings).forEach(([fieldId, mtcField]) => {
          if (mtcData[mtcField] !== undefined && mtcData[mtcField] !== null) {
            console.log(`[DEBUG] Mapping ${fieldId} from ${mtcField}: ${mtcData[mtcField]}`);
            newFormData[fieldId] = mtcData[mtcField];
          }
        });

        // Restore item_description from mtc_items if available
        if (mtcData.item_description) {
          const itemDesc = typeof mtcData.item_description === 'string'
            ? JSON.parse(mtcData.item_description)
            : mtcData.item_description;

          // Map item_description fields back to form fields
          Object.entries(itemDesc).forEach(([key, value]) => {
            newFormData[key] = value || "";
          });
        }

        // Restore heat annexure data
        if (mtcData.attach_heat_annexure && mtcData.heat_annexure_data) {
          setAttachHeatAnnexure(true);
          const heatData = typeof mtcData.heat_annexure_data === 'string'
            ? JSON.parse(mtcData.heat_annexure_data)
            : mtcData.heat_annexure_data;

          if (Array.isArray(heatData)) {
            setHeatAnnexureData(heatData);
          }
        }

        // Fetch fresh TCDS data for the selected TCDS BEFORE setting form data
        let freshTCDSData = null;
        if (mtcData.tcds_id) {
          try {
            // Try different endpoints based on TCDS type
            let endpoints = [`/tcds/${mtcData.tcds_id}`];
            if (mtcData.valve_type === 'gate' || mtcData.valve_type === 'check') {
              endpoints.unshift(`/tcds/api-600/${mtcData.tcds_id}`, `/tcds/api-6d/${mtcData.tcds_id}`);
            } else {
              endpoints.unshift(`/tcds/api-600/${mtcData.tcds_id}`, `/tcds/api-6d/${mtcData.tcds_id}`);
            }

            for (const endpoint of endpoints) {
              try {
                const tcdsResponse = await axios.get(endpoint);
                if (tcdsResponse.data && tcdsResponse.data.success) {
                  freshTCDSData = tcdsResponse.data.data || {};
                  setSelectedTCDSData(freshTCDSData);
                  break;
                }
              } catch (err) {
                continue;
              }
            }
          } catch (err) {
            console.warn('Failed to fetch TCDS data:', err.message);
          }
        }

        // Now override TCDS-sourced fields with fresh TCDS data
        if (freshTCDSData) {
          const fieldMappings = {
            'certificate_no': 'certificate_no',
            'customer': 'customer',
            'customer_name': 'customer',
            'po_no': 'po_no',
            'purchase_order_no': 'po_no',
            'po_date': 'po_date',
            'tcds_no': 'tcds_no',
            'section_number': 'tcds_no',
            'tcds_date': 'tcds_date',
            'gad_no': 'gad_no',
            'gad': 'gad_no',
            'date': 'date',
            'tag_no': 'tag_no',
            'qap_no': 'qap_no',
            'qap': 'qap_no',
            'end_connection': 'end_connection',
            'std_edition': 'std_edition',
            'raised_face_std': 'raised_face_std',
            'trim': 'trim',
            'class': 'class',
            'class_rating': 'class',
            'qty': 'qty',
            'quantity': 'qty',
            'size': 'size',
            'nominal_size': 'size',
            'sr_no': 'sr_no',
            'serial_no': 'sr_no',
            'design_standard': 'design_standard',
            'design_code': 'design_standard',
            'testing_std': 'testing_std',
            'testing_standard': 'testing_standard',
            'test_pressure': 'test_pressure',
            'project_name': 'project_name',
            'testing_duration': 'testing_duration',
            'test_result': 'test_result',
            'hydro_back_seat': 'hydro_back_seat',
            'hydro_body': 'hydro_body',
            'hydro_seat': 'hydro_seat',
            'pneumatic_seat': 'pneumatic_seat',
            'item_desc_material': 'item_desc_material',
            'material_spec': 'material_spec',
            'heat_no': 'heat_no',
            'mtc_ref_no': 'mtc_ref_no',
            'remarks': 'remarks',
            'conformity_statement': 'conformity_statement',
            'customer_item_code': 'customer_item_code',
            'ipc_sr_no': 'ipc_sr_no',
            'ipc_serial_no': 'ipc_sr_no',
            'valve_batch_sr_no': 'valve_batch_sr_no',
            'valve_batch_no': 'valve_batch_sr_no',
            'min_air_supply': 'min_air_supply',
            'min_air_supply_to_actuator': 'min_air_supply',
            'pressure_rating': 'pressure_rating',
            'body_hydrostatic': 'body_hydrostatic',
            'seat_hydrostatic': 'seat_hydrostatic',
            'seat_pneumatic': 'seat_pneumatic',
            'test_duration': 'test_duration',
            'observation': 'observation',
            'remark': 'remark',
            'declaration_of_compliance': 'declaration_of_compliance',
            'compliance_statement': 'declaration_of_compliance',
            'prepared_by': 'prepared_by',
            'prepared_by_name': 'prepared_by',
            'approved_by': 'approved_by',
            'qac_officer': 'qac_officer',
            'Body': 'body_material',
            'body_material': 'body_material',
            'Bonnet': 'bonnet_material',
            'bonnet_material': 'bonnet_material',
            'Wedge': 'wedge_material',
            'disc_material': 'wedge_material',
            'wedge_material': 'wedge_material',
            'Seat Ring': 'seatring_material',
            'seat_ring': 'seatring_material',
            'seat_ring_material': 'seatring_material',
            'Stem': 'stem_material',
            'stem_material': 'stem_material',
            'Packing': 'packing_material',
            'packing_material': 'packing_material',
            'Gasket': 'gasket_material',
            'gasket_material': 'gasket_material',
            'Stud & Nut': 'stud_nut_material',
            'bolting_material': 'stud_nut_material',
            'stud_nut_material': 'stud_nut_material',
          };

          // Helper function to find TCDS value with multiple lookup paths
          const getTCDSValue = (formFieldId, sourceKey) => {
            let value = "";

            // 1. Check dynamic_parameters FIRST with exact capital keys
if (freshTCDSData.dynamic_parameters) {
  try {
    const dynParams = typeof freshTCDSData.dynamic_parameters === 'string'
      ? JSON.parse(freshTCDSData.dynamic_parameters)
      : freshTCDSData.dynamic_parameters;

    const dynKeyMap = {
  // Gate valve keys
  'body_material': 'Body & Bonnet',
  'bonnet_material': 'Body & Bonnet',
  'wedge_material': 'Wedge',
  'seatring_material': 'Seat',
  'stem_material': 'Stem',
  'packing_material': 'Gasket & Packing',
  'gasket_material': 'Gasket & Packing',
  'stud_nut_material': 'Bolting',
  // Check valve keys
  'cover_material': 'Body & Cover',
  'plug_material': 'Disc',
  'hex_bolt_material': 'Bolting',
  // Shared keys
  'size': 'Size',
  'class': 'Class',
  'end_connection': 'End Connection',
  'testing_std': 'Testing Std.',
  'make': 'Make',
  'series': 'Series',
  'type': 'Type',
  'operator': 'Operator',
  'trim': 'Trim No',
};
// Override keys based on valve type
if (valveType === 'check') {
  dynKeyMap['body_material'] = 'Body & Cover';
  dynKeyMap['gasket_material'] = 'Gasket';
  dynKeyMap['stud_nut_material'] = 'Bolting';
}
    const dynKey = dynKeyMap[sourceKey];
    if (dynKey && dynParams[dynKey] !== undefined && dynParams[dynKey] !== null) {
      value = dynParams[dynKey];
    }
  } catch (e) { /* ignore */ }
}

// 2. Check direct property only if dynamic_parameters did not resolve
if (!value && freshTCDSData[sourceKey] !== undefined && freshTCDSData[sourceKey] !== null) {
  value = freshTCDSData[sourceKey];
}
// Try case-insensitive direct property
else if (!value) {
  const caseInsensitiveKey = Object.keys(freshTCDSData).find(
    key => key.toLowerCase() === sourceKey.toLowerCase()
  );
  if (caseInsensitiveKey) {
    value = freshTCDSData[caseInsensitiveKey];
  }
}

// 3. Check mapped key
if (!value) {
  const mappedKey = fieldMappings[sourceKey];
  if (mappedKey && freshTCDSData[mappedKey] !== undefined && freshTCDSData[mappedKey] !== null) {
    value = freshTCDSData[mappedKey];
  }
}

            // 3. Check section_data (flattened)
            if (!value && freshTCDSData.section_data && typeof freshTCDSData.section_data === 'object') {
              Object.values(freshTCDSData.section_data).forEach(section => {
                if (!value && section) {
                  if (section[sourceKey] !== undefined && section[sourceKey] !== null) {
                    value = section[sourceKey];
                  } else {
                    const mappedKey = fieldMappings[sourceKey];
                    if (mappedKey && section[mappedKey] !== undefined && section[mappedKey] !== null) {
                      value = section[mappedKey];
                    }
                  }
                }
              });
            }

            // 4. Check dynamic_parameters (with case-insensitive lookup)
            if (!value && freshTCDSData.dynamic_parameters) {
              try {
                const dynParams = typeof freshTCDSData.dynamic_parameters === 'string'
                  ? JSON.parse(freshTCDSData.dynamic_parameters)
                  : freshTCDSData.dynamic_parameters;
                if (dynParams) {
                  // Try direct access first
                  if (dynParams[sourceKey]) {
                    value = dynParams[sourceKey];
                  }
                  // Try mapped key
                  else {
                    const mappedKey = fieldMappings[sourceKey];
                    if (mappedKey && dynParams[mappedKey]) {
                      value = dynParams[mappedKey];
                    }
                  }
                  // Try case-insensitive lookup for both sourceKey and mappedKey
                  if (!value) {
                    const caseInsensitiveKey = Object.keys(dynParams).find(
                      key => key.toLowerCase() === sourceKey.toLowerCase()
                    );
                    if (caseInsensitiveKey) {
                      value = dynParams[caseInsensitiveKey];
                    } else {
                      const mappedKey = fieldMappings[sourceKey];
                      if (mappedKey) {
                        const caseInsensitiveMappedKey = Object.keys(dynParams).find(
                          key => key.toLowerCase() === mappedKey.toLowerCase()
                        );
                        if (caseInsensitiveMappedKey) {
                          value = dynParams[caseInsensitiveMappedKey];
                        }
                      }
                    }
                  }
                }
              } catch (e) { /* ignore parse errors */ }
            }

            if (!value && (formFieldId === 'size' || formFieldId === 'class')) {
              const keysToCheck = formFieldId === 'size'
                ? ['size', 'nominal_size']
                : ['class', 'class_rating'];

              const extractFromApiData = (apiData) => {
                if (!apiData) return undefined;
                try {
                  const parsed = typeof apiData === 'string' ? JSON.parse(apiData) : apiData;
                  if (!parsed) return undefined;
                  for (const key of keysToCheck) {
                    if (parsed[key] !== undefined && parsed[key] !== null && String(parsed[key]).trim() !== '') {
                      return parsed[key];
                    }
                    const ciKey = Object.keys(parsed).find(k => k.toLowerCase() === key.toLowerCase());
                    if (ciKey && parsed[ciKey] !== undefined && parsed[ciKey] !== null && String(parsed[ciKey]).trim() !== '') {
                      return parsed[ciKey];
                    }
                  }
                } catch (e) {
                  return undefined;
                }
                return undefined;
              };

              const api600Value = extractFromApiData(freshTCDSData.api600_data);
              const api6dValue = extractFromApiData(freshTCDSData.api6d_data);

              if (api600Value !== undefined) {
                value = api600Value;
              } else if (api6dValue !== undefined) {
                value = api6dValue;
              }
            }

            return value;
          };

          // Get all fields and find those with inputType: "tcds"
          const tcdsFields = allFields.filter(field => field.inputType === 'tcds');
          console.log(`[DEBUG] Found ${tcdsFields.length} TCDS-sourced fields to override`);

          // Override TCDS-sourced fields with fresh values
          tcdsFields.forEach(field => {
            const freshValue = getTCDSValue(field.id, field.source);
            if (freshValue && freshValue !== newFormData[field.id]) {
              console.log(`[DEBUG] Overriding ${field.id} from TCDS: old="${newFormData[field.id]}", new="${freshValue}"`);
              newFormData[field.id] = freshValue;
            }
          });
        }

        setFormData(newFormData);

        // Set tag numbers if qty is present
        if (newFormData.qty) {
          const tags = [];
          for (let i = 1; i <= newFormData.qty; i++) {
            tags.push(`F${i}`);
          }
          setTagNumbers(tags);
        }

        // Load Valve Details arrays for Automation MTC if they exist
        if (valveType === "automation") {
          let ipcSrArray = [];
          let valveBatchSrArray = [];

          // Parse ipc_sr_no array if it exists
          if (mtcData.ipc_sr_no) {
            try {
              ipcSrArray = Array.isArray(mtcData.ipc_sr_no)
                ? mtcData.ipc_sr_no
                : typeof mtcData.ipc_sr_no === 'string'
                  ? JSON.parse(mtcData.ipc_sr_no)
                  : [mtcData.ipc_sr_no];
            } catch (e) {
              ipcSrArray = mtcData.ipc_sr_no ? [mtcData.ipc_sr_no] : [];
            }
          }

          // Parse valve_batch_sr_no array if it exists
          if (mtcData.valve_batch_sr_no) {
            try {
              valveBatchSrArray = Array.isArray(mtcData.valve_batch_sr_no)
                ? mtcData.valve_batch_sr_no
                : typeof mtcData.valve_batch_sr_no === 'string'
                  ? JSON.parse(mtcData.valve_batch_sr_no)
                  : [mtcData.valve_batch_sr_no];
            } catch (e) {
              valveBatchSrArray = mtcData.valve_batch_sr_no ? [mtcData.valve_batch_sr_no] : [];
            }
          }

          setIpcSrNoArray(ipcSrArray);
          setValveBatchSrNoArray(valveBatchSrArray);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error loading existing MTC:", error);
      showNotification("error", "Failed to load certificate data");
      setLoading(false);
      // Redirect back to view certificate if load fails
      setTimeout(() => navigate("/view-certificate"), 2000);
    }
  };

  useEffect(() => {
    fetchTCDSList();
    fetchUnitsAndReviewers();
    fetchHeatAnnexureData();

    // Load existing MTC if in edit mode
    if (editMode && mtcId) {
      loadExistingMTC(mtcId);
    }
  }, [editMode, mtcId]);

  // Fetch Heat Annexure data (components and master data)
  const fetchHeatAnnexureData = async () => {
    try {
      // Fetch components from heat annexure options (which reads from standards table)
      const optionsResponse = await axios.get("/heat-annexure/options");
      if (optionsResponse.data.success && optionsResponse.data.data) {
        // Transform components to have id and value properties
        const components = optionsResponse.data.data.components.map((comp, idx) => ({
          id: idx + 1,
          value: comp,
          name: comp
        }));
        setHeatAnnexureComponents(components);
      }
      
      // Fetch all heat annexure master data (for auto-fill when heat number selected)
      const heatMasterResponse = await axios.get("/heat-annexure");
      if (heatMasterResponse.data.success && heatMasterResponse.data.data) {
        setHeatAnnexureMaster(heatMasterResponse.data.data);
      }
    } catch (error) {
      console.warn("Could not fetch heat annexure data:", error);
    }
  };

  // Generate heat annexure rows based on quantity and selected components
  // Valve No. is now left empty for manual entry
  const generateHeatAnnexureRows = (qty, components) => {
    const rows = [];

    for (let valveIndex = 0; valveIndex < qty; valveIndex++) {
      components.forEach(component => {
        rows.push({
          valveNo: "", // Manual entry - left empty for user to fill
          component: component,
          heatNumber: "",
          material: "",
          tcNo: "",
          vendorName: "",
          attachmentId: null,
          attachmentFilename: ""
        });
      });
    }
    setHeatAnnexureData(rows);
  };

  // Handle heat number selection - do NOT auto-fill yet
  const handleHeatNumberChange = (rowIndex, heatNumber) => {
    const newData = [...heatAnnexureData];
    newData[rowIndex].heatNumber = heatNumber;
    // Clear material, tcNo, and vendorName - wait for TC No. selection
    newData[rowIndex].material = "";
    newData[rowIndex].tcNo = "";
    newData[rowIndex].vendorName = "";
    setHeatAnnexureData(newData);
  };

  // Handle TC No. selection - auto-fill material and vendor name from master
  const handleTCNumberChange = (rowIndex, tcNo) => {
    const newData = [...heatAnnexureData];
    newData[rowIndex].tcNo = tcNo;

    // Find matching record in heat annexure master with ALL three criteria
    const masterRecord = heatAnnexureMaster.find(
      m => m.component === newData[rowIndex].component &&
           m.heat_number === newData[rowIndex].heatNumber &&
           m.tc_no === tcNo
    );

    if (masterRecord) {
      newData[rowIndex].material = masterRecord.material || "";
      newData[rowIndex].vendorName = masterRecord.vendor_name || "";
      newData[rowIndex].attachmentId = masterRecord.attachment_filename ? masterRecord.id : null;
      newData[rowIndex].attachmentFilename = masterRecord.attachment_filename || "";
    } else {
      newData[rowIndex].material = "";
      newData[rowIndex].vendorName = "";
      newData[rowIndex].attachmentId = null;
      newData[rowIndex].attachmentFilename = "";
    }

    setHeatAnnexureData(newData);
  };

  // Handle component selection change
  const handleComponentSelectionChange = (component, isChecked) => {
    let newComponents;
    if (isChecked) {
      newComponents = [...selectedHeatComponents, component];
    } else {
      newComponents = selectedHeatComponents.filter(c => c !== component);
    }
    setSelectedHeatComponents(newComponents);
    generateHeatAnnexureRows(heatAnnexureQuantity, newComponents);
  };

  // Handle heat annexure quantity change
  const handleHeatAnnexureQuantityChange = (qty) => {
    const numQty = parseInt(qty) || 1;
    setHeatAnnexureQuantity(numQty);
    generateHeatAnnexureRows(numQty, selectedHeatComponents);
  };

  // Get heat numbers available for a specific component from master
  const getHeatNumbersForComponent = (component) => {
    return heatAnnexureMaster
      .filter(m => m.component === component)
      .map(m => m.heat_number)
      .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
  };

  // Get TC numbers available for a specific component and heat number from master
  const getTCNumbersForComponentAndHeat = (component, heatNumber) => {
    return heatAnnexureMaster
      .filter(m => m.component === component && m.heat_number === heatNumber)
      .map(m => m.tc_no)
      .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
  };

  // Open heat annexure attachment preview in a new tab (with auth)
  const openHeatAttachmentPreview = async (e, configId) => {
    e.preventDefault();
    try {
      const response = await axios.get(`/heat-annexure/${configId}/attachment`, {
        responseType: "blob"
      });
      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("Failed to open attachment:", err);
      setNotification({ show: true, type: "error", message: "Failed to open attachment preview" });
    }
  };

  // Extract and format valve details from TCDS data
  const getFormattedValveDetails = () => {
    if (!selectedTCDSData) return null;

    const vatValues = {
      'make': 'MAKE',
      'series': 'SERIES',
      'type': 'TYPE',
      'end_connection': 'END CONNECTION',
      'size': 'SIZE',
      'class': 'CLASS',
      'body': 'BODY',
      'disc': 'DISC',
      'seat': 'SEAT',
      'spring': 'SPRING',
      'operator': 'OPERATOR'
    };

    const details = [];
    Object.keys(vatValues).forEach(key => {
      const label = vatValues[key];
      const value = selectedTCDSData[key] || selectedTCDSData[label] || '';
      if (value) {
        details.push(`${label}: ${value}`);
      }
    });

    return details.length > 0 ? details.join(', ') : null;
  };

            
            
   // Recursively flatten nested objects into key-value pairs
  // Extract and format KEY: VALUE pairs from parameters string
 const flattenObject = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return '';
    }

    const result = [];
    const excludeKeys = ['id', 'tcds_id', 'type', 'accessoryName', 'component', 'created_at', 'updated_at', 'createdAt', 'updatedAt', 'accessoryId'];

    Object.keys(obj).forEach(key => {
      if (excludeKeys.includes(key)) return;

      const value = obj[key];
      if (!value || value === '') return;

      // Handle parameters array
      if (Array.isArray(value)) {
        const paramPairs = value
          .filter(item => item && item.name && item.value)
          .map(item => `${item.name.toUpperCase()}: ${item.value}`)
          .join(' , ');
        if (paramPairs) result.push(paramPairs);
        return;
      }

      // Regular string values
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed && trimmed !== 'NR') {
          result.push(`${key.toUpperCase().replace(/_/g, ' ')}: ${trimmed}`);
        }
      }
    });

    return result.join(' , ');
  };

  const getAccessoryDetails = () => {
    if (!selectedTCDSData || !selectedTCDSData.accessories_data) return {};

    let accessoriesData = selectedTCDSData.accessories_data;
    
    if (typeof accessoriesData === 'string') {
      try {
        accessoriesData = JSON.parse(accessoriesData);
      } catch (e) {
        return {};
      }
    }

    const accessoryDetailsMap = {};

    if (Array.isArray(accessoriesData)) {
      accessoriesData.forEach(acc => {
        if (!acc || typeof acc !== 'object') return;

        const accType = (acc.accessoryName || acc.component || acc.type || acc.name || '').toLowerCase().trim();
        
        if (accType && !accessoryDetailsMap[accType]) {
          const details = flattenObject(acc);
          if (details) {
            accessoryDetailsMap[accType] = details;
          }
        }
      });
    }

    return accessoryDetailsMap;
  };      

            

  // Fetch units and reviewers from API for dropdowns
  const fetchUnitsAndReviewers = async () => {
    try {
      // Fetch units from units endpoint (accessible without admin role)
      const unitsResponse = await axios.get("/units");
      if (unitsResponse.data.success && unitsResponse.data.data) {
        const units = unitsResponse.data.data.map(unit => ({
          // Map unit names to certificate format: UNIT-1 -> U1, UNIT-2 -> U2
          value: unit.name.includes("1") || unit.name.toLowerCase().includes("a") ? "U1" :
                 unit.name.includes("2") || unit.name.toLowerCase().includes("b") ? "U2" :
                 `U${unit.id}`,
          label: unit.name,
          id: unit.id
        }));
        setUnitOptions(units);
      }

      // Fetch reviewers from tcds-reviewers endpoint (Section 21 - Reviewed/Participate By)
      const reviewersResponse = await axios.get("/tcds-reviewers");
      if (reviewersResponse.data.success && reviewersResponse.data.data) {
        const reviewers = reviewersResponse.data.data.map(reviewer => ({
          value: reviewer.name,
          label: reviewer.name,
          id: reviewer.id,
          signature_path: reviewer.signature_path
        }));
        setReviewerOptions(reviewers);
      }

      // Fetch Declaration of Compliance options from Standard Master
      try {
        const declarationResponse = await axios.get("/standards/declaration_of_compliance");
        if (declarationResponse.data.success && declarationResponse.data.data) {
          const declarations = declarationResponse.data.data.map(item => ({
            value: item.value || item.name,
            label: item.value || item.name,
            id: item.id
          }));
          setDeclarationOptions(declarations);
        }
      } catch (declError) {
        console.warn("Could not fetch declaration of compliance options:", declError);
      }

      // Fetch Testing Standard options from Standard Master
      try {
        const testingStdResponse = await axios.get("/standards/testing_standard");
        if (testingStdResponse.data.success && testingStdResponse.data.data) {
          const testingStds = testingStdResponse.data.data.map(item => ({
            value: item.value || item.name,
            label: item.value || item.name,
            id: item.id
          }));
          setTestingStandardOptions(testingStds);
        }
      } catch (testError) {
        console.warn("Could not fetch testing standard options:", testError);
      }
    } catch (error) {
      console.warn("Could not fetch units or reviewers:", error);
      // Use fallback options if API fails
      setUnitOptions([
        { value: "U1", label: "Unit 1", id: 1 },
        { value: "U2", label: "Unit 2", id: 2 }
      ]);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: "", message: "" }), 3000);
  };

  // Fetch Testing Details from Testing Parameter Master based on SIZE, CLASS, TESTING STD
  const fetchTestingDetails = async (size, classVal, testingStd) => {
    if (!size || !classVal || !testingStd) return;
    
    try {
      const params = {
        size: size,
        class: classVal,
        testing_standard: testingStd
      };
      
      const response = await axios.get("/testing-parameters", { params });
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const testingData = response.data.data[0];
        
        // Auto-fill testing detail fields for all valve types
        // Map API response fields to form field names
        setFormData(prev => ({
          ...prev,
          // GGC valve fields (Gate, Globe, Check) - TEST PRESSURE
          test_pressure_back_seat: testingData.hydro_back_seat_pressure || prev.test_pressure_back_seat,
          test_pressure_body: testingData.hydro_body_pressure || prev.test_pressure_body,
          test_pressure_seat: testingData.hydro_seat_pressure || prev.test_pressure_seat,
          test_pressure_pneumatic: testingData.pneumatic_seat_pressure || prev.test_pressure_pneumatic,
          // GGC valve fields - TESTING DURATION
          testing_duration_back_seat: testingData.hydro_back_seat_duration || prev.testing_duration_back_seat,
          testing_duration_body: testingData.hydro_body_duration || prev.testing_duration_body,
          testing_duration_seat: testingData.hydro_seat_duration || prev.testing_duration_seat,
          testing_duration_pneumatic: testingData.pneumatic_seat_duration || prev.testing_duration_pneumatic,
          // GGC valve fields - TEST RESULT (acceptance criteria)
          test_result_back_seat: testingData.hydro_back_seat_acceptance || prev.test_result_back_seat,
          test_result_body: testingData.hydro_body_acceptance || prev.test_result_body,
          test_result_seat: testingData.hydro_seat_acceptance || prev.test_result_seat,
          test_result_pneumatic: testingData.pneumatic_seat_acceptance || prev.test_result_pneumatic,
          // Ball valve fields
          hydro_shell_pressure: testingData.hydro_body_pressure || prev.hydro_shell_pressure,
          hydro_shell_duration: testingData.hydro_body_duration || prev.hydro_shell_duration,
          hydro_seat_pressure: testingData.hydro_seat_pressure || prev.hydro_seat_pressure,
          hydro_seat_duration: testingData.hydro_seat_duration || prev.hydro_seat_duration,
          pneu_seat_pressure: testingData.pneumatic_seat_pressure || prev.pneu_seat_pressure,
          pneu_seat_duration: testingData.pneumatic_seat_duration || prev.pneu_seat_duration,
          // Legacy field names for compatibility
          hydro_back_seat: testingData.hydro_back_seat_pressure || prev.hydro_back_seat,
          hydro_body: testingData.hydro_body_pressure || prev.hydro_body,
          hydro_seat: testingData.hydro_seat_pressure || prev.hydro_seat,
          pneumatic_seat: testingData.pneumatic_seat_pressure || prev.pneumatic_seat,
          // Automation form fields
          pressure_rating: testingData.hydro_body_pressure || prev.pressure_rating,
          body_hydrostatic: testingData.hydro_body_pressure || prev.body_hydrostatic,
          seat_hydrostatic: testingData.hydro_seat_pressure || prev.seat_hydrostatic,
          seat_pneumatic: testingData.pneumatic_seat_pressure || prev.seat_pneumatic,
          test_duration: testingData.hydro_body_duration || prev.test_duration
        }));
        
        console.log("[MTC] Auto-filled testing details from Testing Parameter Master:", testingData);
        showNotification("success", "Testing details auto-filled from master");
      } else {
        console.log("[MTC] No matching testing parameters found for:", { size, classVal, testingStd });
      }
    } catch (error) {
      console.warn("[MTC] Could not fetch testing parameters:", error);
    }
  };

  const fetchTCDSList = async () => {
    try {
      setLoading(true);
      // Fetch ALL TCDS types since filtering is done by recordLooksLikeSelectedValve
      // based on content (type, accessories_data, etc.)
      const allTcdsTypes = ["Automation/Standard", "API 600", "API 6D"];
      const responses = await Promise.all(
        allTcdsTypes.map((type) =>
          axios.get("/tcds", {
            params: { tcds_type: type },
          })
        )
      );

      const merged = responses.flatMap((response) =>
        response.data.success ? (response.data.data || []) : []
      );

      const uniqueById = Array.from(new Map(merged.map((row) => [row.id, row])).values());

      // Debug logging - show all TCDS records before filtering
      console.log(`[MTC Filter] Total TCDS records fetched: ${uniqueById.length}`);
      console.log(`[MTC Filter] Current valveType: ${valveType}`);
      
      // Keep only records matching the currently selected valve card,
      // filtering is done by recordLooksLikeSelectedValve based on:
      // - Gate MTC: API 600 + Automation/Standard with gate valve
      // - Check MTC: API 6D + Automation/Standard with check valve  
      // - Globe MTC: Automation/Standard with globe valve (+ accessories ok)
      // - Ball MTC: Automation/Standard with ball valve (+ accessories ok)
      // - LSB MTC: Automation/Standard with LSB accessory ONLY (no valve)
      // - Actuator MTC: Automation/Standard with Actuator accessory ONLY (no valve)
      // - Automation MTC: Automation/Standard with >1 accessory types (no valve)
      const filtered = uniqueById.filter((row) => {
        return recordLooksLikeSelectedValve(row);
      });

      console.log(`[MTC Filter] ${valveType} - Filtered TCDS count: ${filtered.length} / ${uniqueById.length}`);

      setTcdsList(filtered);
    } catch (error) {
      console.error("Failed to fetch TCDS list:", error);
      showNotification("error", "Failed to load TCDS list");
    } finally {
      setLoading(false);
    }
  };

  const handleTCDSSelect = async (tcds) => {
    try {
      setFetchingTCDS(true);
      
      // Determine which API endpoint to use based on TCDS type
      // For check valves, exclude API 600 - only keep standard/automation and api 6d
      // For gate valves, exclude API 6D - only keep standard/automation and api 600
      // For all other valves (globe, ball, lsb, actuator, automation), exclude both API 6D and API 600 - only keep standard/automation
      let endpoint = `/tcds/${tcds.id}`; // Default endpoint
      if (tcds.tcds_type === 'API 600' && valveType !== 'check' && valveType !== 'gate') {
        endpoint = `/tcds/api-600/${tcds.id}`;
      } else if (tcds.tcds_type === 'API 6D' && valveType !== 'gate') {
        endpoint = `/tcds/api-6d/${tcds.id}`;
      }
      
      const response = await axios.get(endpoint);

      if (response.data.success) {
        const tcdsData = response.data.data;
        console.log('[TCDS API] Raw TCDS data received - Size:', tcdsData.size, 'Class:', tcdsData.class);
        setSelectedTCDS(tcds);
        setSelectedTCDSData(tcdsData); // Store full TCDS data for PDF
        // Check if TCDS has a valve OR has any accessories (with or without valve)
        const hasValve = hasValveSelected(tcds);
        const hasAccessories = tcdsData?.accessories_data && tcdsData.accessories_data.length > 0;
        setHasValveInTCDS(hasValve || hasAccessories);

        // Populate form data from TCDS
        const newFormData = { ...formData };
        
        // Handle unit_id from TCDS - convert to U1/U2 format
        if (tcdsData.unit_id) {
          // Match unit_id with unitOptions to get the correct value
          const matchedUnit = unitOptions.find(u => u.id === tcdsData.unit_id);
          if (matchedUnit) {
            newFormData.unit = matchedUnit.value;
          } else {
            // Fallback: assume unit_id 1 = U1, 2 = U2
            newFormData.unit = tcdsData.unit_id === 1 ? 'U1' : 
                              tcdsData.unit_id === 2 ? 'U2' : 
                              `U${tcdsData.unit_id}`;
          }
        }
        
        // Field mappings from TCDS to MTC form
        const fieldMappings = {
          'certificate_no': 'certificate_no',
          'customer': 'customer',
          'customer_name': 'customer',
          'po_no': 'po_no',
          'purchase_order_no': 'po_no',
          'po_date': 'po_date',
          'tcds_no': 'tcds_no',
          'section_number': 'tcds_no',
          'tcds_date': 'tcds_date',
          'gad_no': 'gad_no',
          'gad': 'gad_no',
          'date': 'date',
          'tag_no': 'tag_no',
          'qap_no': 'qap_no',
          'qap': 'qap_no',
          'end_connection': 'end_connection',
          'std_edition': 'std_edition',
          'raised_face_std': 'raised_face_std',
          'trim': 'trim',
          'class': 'class',
          'class_rating': 'class',
          'qty': 'qty',
          'quantity': 'qty',
          'size': 'size',
          'nominal_size': 'size',
          'sr_no': 'sr_no',
          'serial_no': 'sr_no',
          'design_standard': 'design_standard',
          'design_code': 'design_standard',
          'testing_std': 'testing_std',
          'testing_standard': 'testing_standard',
          'test_pressure': 'test_pressure',
          'project_name': 'project_name',
          'testing_duration': 'testing_duration',
          'test_result': 'test_result',
          'hydro_back_seat': 'hydro_back_seat',
          'hydro_body': 'hydro_body',
          'hydro_seat': 'hydro_seat',
          'pneumatic_seat': 'pneumatic_seat',
          'item_desc_material': 'item_desc_material',
          'material_spec': 'material_spec',
          'heat_no': 'heat_no',
          'mtc_ref_no': 'mtc_ref_no',
          'remarks': 'remarks',
          'conformity_statement': 'conformity_statement',
          'customer_item_code': 'customer_item_code',
          // Automation fields
          'ipc_sr_no': 'ipc_sr_no',
          'ipc_serial_no': 'ipc_sr_no',
          'valve_batch_sr_no': 'valve_batch_sr_no',
          'valve_batch_no': 'valve_batch_sr_no',
          'tag_no': 'tag_no',
          'testing_standard': 'testing_standard',
          'min_air_supply': 'min_air_supply',
          'min_air_supply_to_actuator': 'min_air_supply',
          'pressure_rating': 'pressure_rating',
          'body_hydrostatic': 'body_hydrostatic',
          'seat_hydrostatic': 'seat_hydrostatic',
          'seat_pneumatic': 'seat_pneumatic',
          'test_duration': 'test_duration',
          'observation': 'observation',
          'remark': 'remark',
          'remarks': 'remark',
          'declaration_of_compliance': 'declaration_of_compliance',
          'compliance_statement': 'declaration_of_compliance',
          'prepared_by': 'prepared_by',
          'prepared_by_name': 'prepared_by',
          'approved_by': 'approved_by',
          'qac_officer': 'qac_officer',
          'Body': 'body',
          'body_material': 'body',
          'Bonnet': 'bonnet',
          'bonnet_material': 'bonnet',
          'Wedge': 'wedge',
          'disc_material': 'wedge',
          'Seat Ring': 'seat_ring',
          'seat_ring_material': 'seat_ring',
          'Stem': 'stem',
          'stem_material': 'stem',
          'Packing': 'packing',
          'packing_material': 'packing',
          'Gasket': 'gasket',
          'gasket_material': 'gasket',
          'Stud & Nut': 'stud_nut',
          'bolting_material': 'stud_nut',
        };

        // Helper to find value in various data sources
        const findValue = (sourceKey, dataObj) => {
          let value = "";
          
          // Check field mappings first
          const mappedKey = fieldMappings[sourceKey];
          
          // 1. Check direct property
          if (dataObj[mappedKey] !== undefined && dataObj[mappedKey] !== null) {
            value = dataObj[mappedKey];
          }
          // Also check original sourceKey
          else if (dataObj[sourceKey] !== undefined && dataObj[sourceKey] !== null) {
            value = dataObj[sourceKey];
          }
          // 2. Check section_data (flattened)
          else if (dataObj.section_data && typeof dataObj.section_data === 'object') {
            Object.values(dataObj.section_data).forEach(section => {
              if (section) {
                if (section[mappedKey] !== undefined && section[mappedKey] !== null) {
                  value = section[mappedKey];
                } else if (section[sourceKey] !== undefined && section[sourceKey] !== null) {
                  value = section[sourceKey];
                }
              }
            });
          }
          // 3. Check dynamic_parameters (JSON string or object)
          else if (dataObj.dynamic_parameters) {
            try {
              const dynParams = typeof dataObj.dynamic_parameters === 'string' 
                ? JSON.parse(dataObj.dynamic_parameters) 
                : dataObj.dynamic_parameters;
              if (dynParams) {
                if (dynParams[mappedKey]) {
                  value = dynParams[mappedKey];
                } else if (dynParams[sourceKey]) {
                  value = dynParams[sourceKey];
                }
              }
            } catch (e) { /* ignore parse errors */ }
          }
          // 4. Check accessories_data (JSON string or object)
          else if (dataObj.accessories_data) {
            try {
              const accData = typeof dataObj.accessories_data === 'string'
                ? JSON.parse(dataObj.accessories_data)
                : dataObj.accessories_data;
              if (Array.isArray(accData)) {
                accData.forEach(acc => {
                  if (acc) {
                    if (acc[mappedKey]) {
                      value = acc[mappedKey];
                    } else if (acc[sourceKey]) {
                      value = acc[sourceKey];
                    }
                  }
                });
              }
            } catch (e) { /* ignore parse errors */ }
          }
          // 5. Check api600_data for API 600 records
          else if (dataObj.api600_data) {
            try {
              const api600Data = typeof dataObj.api600_data === 'string' 
                ? JSON.parse(dataObj.api600_data) 
                : dataObj.api600_data;
              if (api600Data) {
                if (api600Data[mappedKey]) {
                  value = api600Data[mappedKey];
                } else if (api600Data[sourceKey]) {
                  value = api600Data[sourceKey];
                }
              }
            } catch (e) { /* ignore parse errors */ }
          }
          // 6. Check common field aliases
          else if (sourceKey === 'Body' && dataObj.body_material) {
            value = dataObj.body_material;
          } else if (sourceKey === 'Bonnet' && dataObj.bonnet_material) {
            value = dataObj.bonnet_material;
          } else if ((sourceKey === 'Seat' || sourceKey === 'SEATRING') && dataObj.seat_material) {
            value = dataObj.seat_material;
          } else if (sourceKey === 'Stem' && dataObj.stem_material) {
            value = dataObj.stem_material;
          } else if ((sourceKey === 'Wedge' || sourceKey === 'Disc') && dataObj.disc_material) {
            value = dataObj.disc_material;
          } else if ((sourceKey === 'Gasket') && dataObj.gasket_material) {
            value = dataObj.gasket_material;
          } else if ((sourceKey === 'Packing') && dataObj.packing_material) {
            value = dataObj.packing_material;
          } else if ((sourceKey === 'Stud & Nut' || sourceKey === 'Bolting') && dataObj.bolting_material) {
            value = dataObj.bolting_material;
          }
          
          return value;
        };
        
        // First, try to autofill from valve type API configuration
        // This requires finding matching valve type
        try {
          // Get active API configurations to find matching valve type
          const apiConfigResponse = await axios.get("/api-configurations/active");
          const apiConfigs = apiConfigResponse.data.data || [];
          
          // Try to find matching configuration based on TCDS data
          const matchingConfig = apiConfigs.find(config => {
            const keysToMatch = ['make', 'series', 'type', 'end_connection', 'size', 'class'];
            return keysToMatch.every(key => {
              const tcdsValue = String(tcdsData[key] || '').toLowerCase().trim();
              const configValue = String(config[key] || '').toLowerCase().trim();
              return !tcdsValue || tcdsValue === configValue;
            });
          });
          
          if (matchingConfig) {
            // Autofill from API configuration if not already set
            const configFields = [
              { source: 'make', target: 'make' },
              { source: 'series', target: 'series' },
              { source: 'type', target: 'type' },
              { source: 'size', target: 'size' },
              { source: 'class', target: 'class' },
              { source: 'body', target: 'Body' },
              { source: 'disc', target: 'Disc' },
              { source: 'stem', target: 'Stem' },
              { source: 'seat', target: 'Seat' },
              { source: 'operator', target: 'operator' },
              { source: 'painting_details', target: 'painting_details' },
              { source: 'flow_direction', target: 'flow_direction' },
              { source: 'operation', target: 'operation' },
              { source: 'end_connection', target: 'end_connection' },
            ];
            
            configFields.forEach(field => {
              if (matchingConfig[field.source] && !newFormData[field.target]) {
                newFormData[field.target] = matchingConfig[field.source];
              }
            });
          }
        } catch (apiError) {
          console.warn('Could not fetch API configurations for autofill:', apiError);
        }
        
        // Parse dynamic_parameters and api600_data
        let dynParams = {};
        let api600Data = {};
        
        try {
          if (tcdsData.dynamic_parameters) {
            dynParams = typeof tcdsData.dynamic_parameters === 'string' 
              ? JSON.parse(tcdsData.dynamic_parameters) 
              : tcdsData.dynamic_parameters;
          }
        } catch (e) { dynParams = {}; }
        
        try {
          if (tcdsData.api600_data) {
            api600Data = typeof tcdsData.api600_data === 'string' 
              ? JSON.parse(tcdsData.api600_data) 
              : tcdsData.api600_data;
          }
        } catch (e) { api600Data = {}; }
        
        // Parse api6d_data for check valves
        let api6dData = {};
        try {
          if (tcdsData.api6d_data) {
            api6dData = typeof tcdsData.api6d_data === 'string' 
              ? JSON.parse(tcdsData.api6d_data) 
              : tcdsData.api6d_data;
          }
        } catch (e) { api6dData = {}; }
        
        // Helper to get value from multiple sources
        const getFieldValue = (...sources) => {
          for (const source of sources) {
            if (source && String(source).trim()) {
              return String(source).trim();
            }
          }
          return '';
        };

        // Helper to get value from dynParams with case-insensitive key lookup
        const getDynParamValue = (key) => {
          if (!dynParams || !key) return undefined;
          // Try exact match first
          if (dynParams[key] !== undefined) {
            return dynParams[key];
          }
          // Try case-insensitive match
          const matchingKey = Object.keys(dynParams).find(k => k.toLowerCase() === key.toLowerCase());
          return matchingKey ? dynParams[matchingKey] : undefined;
        };
        
        const getDynParamValueByKeys = (...keys) => {
          for (const key of keys) {
            const value = getDynParamValue(key);
            if (value !== undefined && value !== null && String(value).trim() !== '') {
              return value;
            }
          }
          return undefined;
        };
            
         // Helper to get value from accessories_data[0].parameters array by parameter name
          const getAccParamValue = (name) => {
            try {
              const accData = typeof tcdsData.accessories_data === 'string'
                ? JSON.parse(tcdsData.accessories_data)
                : tcdsData.accessories_data;
              if (!Array.isArray(accData) || accData.length === 0) return undefined;
              const acc = accData[0];
              if (!acc.parameters || !Array.isArray(acc.parameters)) return undefined;
              const param = acc.parameters.find(p => p.name && p.name.toUpperCase() === name.toUpperCase());
              return param ? param.value : undefined;
            } catch (e) {
              return undefined;
            }
          };
        
        // Direct field mapping from TCDS to MTC form
        const directMappings = {
          // Header - From TCDS / Database
          certificate_no: getFieldValue(tcdsData.certificate_no),
          customer: getFieldValue(tcdsData.customer_name, tcdsData.customer),
          po_no: getFieldValue(tcdsData.purchase_order_no, tcdsData.po_no, tcdsData.poNumber),
          po_date: getFieldValue(tcdsData.po_date, tcdsData.poDate),
          tcds_no: getFieldValue(tcdsData.tcds_no, tcdsData.section_number, tcdsData.tcdsNumber),
          tcds_date: getFieldValue(tcdsData.tcds_date, tcdsData.tcdsDate, tcdsData.tcds_release_date),
          // From TCDS / Database - Valve Specs
          tag_no: getFieldValue(tcdsData.tag_no, tcdsData.tagNumber, getDynParamValue('tag_no')),
          trim: getFieldValue(tcdsData.trim, tcdsData.Trim, getDynParamValue('trim')),
          size: getFieldValue(
            tcdsData.size,
            tcdsData.nominal_size,
            getDynParamValueByKeys('size', 'nominal_size', 'nominal size'),
            api600Data.size,
            api600Data.nominal_size,
            api6dData.size,
            api6dData.nominal_size
          ),
          class: getFieldValue(
            tcdsData.class,
            tcdsData.class_rating,
            getDynParamValueByKeys('class', 'class_rating', 'class rating'),
            api600Data.class,
            api600Data.class_rating,
            api6dData.class,
            api6dData.class_rating
          ),
          end_connection: getFieldValue(tcdsData.end_connection, getDynParamValue('end_connection'), api600Data.end_connection),
          std_edition: getFieldValue(tcdsData.std_edition, getDynParamValue('std_edition'), api600Data.std),
          raised_face_std: getFieldValue(tcdsData.raised_face_std, getDynParamValue('raised_face_std'), api600Data.rf_std),
          make: getFieldValue(tcdsData.make, getDynParamValue('make')),
          series: getFieldValue(tcdsData.series, getDynParamValue('series')),
          type: getFieldValue(tcdsData.type, getDynParamValue('type')),
          operator: getFieldValue(tcdsData.operator, getDynParamValue('operator')),
          painting_details: getFieldValue(tcdsData.painting_details, getDynParamValue('painting_details')),
          flow_direction: getFieldValue(tcdsData.flow_direction, getDynParamValue('flow_direction')),
          operation: getFieldValue(tcdsData.operation, getDynParamValue('operation')),
          // Item Description - From TCDS / dynamic_parameters / api600_data / api6d_data
          item_desc_material: getFieldValue(tcdsData.item_desc_material, getDynParamValue('item_description'), api600Data.item_description, api6dData.item_description),
          material_spec: getFieldValue(tcdsData.material_spec, getDynParamValue('material_spec'), api600Data.material, api6dData.material_spec),
          
            // Gate valve components - From TCDS
          body: getFieldValue(getDynParamValue('Body & Bonnet'), getDynParamValue('body'), tcdsData.body_material, tcdsData.Body, api600Data.body),
          bonnet: getFieldValue(getDynParamValue('Body & Bonnet'), getDynParamValue('bonnet'), tcdsData.bonnet_material, tcdsData.Bonnet, api600Data.bonnet),
          wedge: getFieldValue(getDynParamValue('Wedge'), getDynParamValue('wedge'), tcdsData.disc_material, tcdsData.Wedge, api600Data.disk),
          seat_ring: getFieldValue(getDynParamValue('Seat'), getDynParamValue('seat_ring'), tcdsData.seat_material, tcdsData.seat_ring_material, api600Data.seat_ring),
          stem: getFieldValue(getDynParamValue('Stem'), getDynParamValue('stem'), tcdsData.stem_material, tcdsData.Stem, api600Data.stem),
          packing: getFieldValue(getDynParamValue('Gasket & Packing'), getDynParamValue('packing'), tcdsData.packing_material, tcdsData.Packing),
          gasket: getFieldValue(getDynParamValue('Gasket & Packing'), getDynParamValue('gasket'), tcdsData.gasket_material, tcdsData.Gasket, api600Data.gasket),
		stud_nut: getFieldValue(getDynParamValue('Bolting'), getDynParamValue('stud_nut'), tcdsData.bolting_material, tcdsData['Stud & Nut'], api600Data.bolting),
            
            // Globe valve components - From TCDS
          disk: getFieldValue(tcdsData.disc_material, tcdsData.Disk, getDynParamValue('disk'), api600Data.disk),
          seat: getFieldValue(tcdsData.seat_material, tcdsData.Seat, getDynParamValue('seat'), api600Data.seat),
          bolting: getFieldValue(tcdsData.bolting_material, tcdsData.Bolting, getDynParamValue('bolting'), api600Data.bolting),
          stem_nut: getFieldValue(tcdsData.stem_nut, tcdsData['Stem Nut'], getDynParamValue('stem_nut')),
          eye_bolt: getFieldValue(tcdsData.eye_bolt, tcdsData['Eye Bolt'], getDynParamValue('eye_bolt')),
          gland_flange: getFieldValue(tcdsData.gland_flange, tcdsData['Gland Flange'], getDynParamValue('gland_flange')),
          back_seat_bushing: getFieldValue(tcdsData.back_seat_bushing, getDynParamValue('back_seat_bushing')),
          drain_plug: getFieldValue(tcdsData.drain_plug, getDynParamValue('drain_plug')),
          adapter_flange: getFieldValue(tcdsData.adapter_flange, getDynParamValue('adapter_flange')),
          
            // Check valve components - From TCDS / api6d_data
          hinge: getFieldValue(tcdsData.hinge, getDynParamValue('hinge'), api6dData.hinge),
          stop_plate: getFieldValue(tcdsData.stop_plate, getDynParamValue('stop_plate'), api6dData.stop_plate),
          hinge_pin: getFieldValue(tcdsData.hinge_pin, getDynParamValue('hinge_pin'), api6dData.hinge_pin),
          disc_arm: getFieldValue(tcdsData.disc_arm, getDynParamValue('disc_arm'), api6dData.disc_arm),
          back_seat: getFieldValue(tcdsData.back_seat, getDynParamValue('back_seat'), api6dData.back_seat),
          
            // Ball valve components - From TCDS
          ball: getFieldValue(tcdsData.ball, tcdsData.Ball, getDynParamValue('ball')),
          stem_seal: getFieldValue(tcdsData.stem_seal, getDynParamValue('stem_seal')),
          handle: getFieldValue(tcdsData.handle, getDynParamValue('handle')),
          handle_nut: getFieldValue(tcdsData['handle Nut'], getDynParamValue('handle_nut')),
          anti_static: getFieldValue(tcdsData.anti_static, getDynParamValue('anti_static')),
          body_seal: getFieldValue(tcdsData.body_seal, getDynParamValue('body_seal')),
          // Additional Testing from TCDS
          hydro_back_seat: getFieldValue(tcdsData.hydro_back_seat, getDynParamValue('hydro_back_seat')),
          hydro_body: getFieldValue(tcdsData.hydro_body, getDynParamValue('hydro_body')),
          hydro_seat: getFieldValue(tcdsData.hydro_seat, getDynParamValue('hydro_seat')),
          pneumatic_seat: getFieldValue(tcdsData.pneumatic_seat, getDynParamValue('pneumatic_seat')),
          design_standard: getFieldValue(tcdsData.design_standard, getDynParamValue('design_standard'), api600Data.design_standard),
          testing_std: getFieldValue(tcdsData.testing_std, getDynParamValue('testing_std'), api600Data.test_standard),
          test_pressure: getFieldValue(tcdsData.test_pressure, getDynParamValue('test_pressure'), api600Data.test_pressure),
          
            // New material table format - body_material, body_heat_no, body_mtc_no, etc.
         
            // BODY
          body_material: getFieldValue(getDynParamValue('Body & Bonnet'), getDynParamValue('body'), tcdsData.body_material, tcdsData.Body, api600Data.body),
          // BONNET
          bonnet_material: getFieldValue(getDynParamValue('Body & Bonnet'), getDynParamValue('bonnet'), tcdsData.bonnet_material, tcdsData.Bonnet, api600Data.bonnet),
          // WEDGE
          wedge_material: getFieldValue(getDynParamValue('Wedge'), getDynParamValue('wedge'), tcdsData.disc_material, tcdsData.Wedge, api600Data.disk),
          // SEATRING
          seatring_material: getFieldValue(getDynParamValue('Seat'), getDynParamValue('seat_ring'), tcdsData.seat_material, tcdsData.seat_ring_material, tcdsData['Seat Ring'], api600Data.seat_ring),
          // STEM
          stem_material: getFieldValue(getDynParamValue('Stem'), getDynParamValue('stem'), tcdsData.stem_material, tcdsData.Stem, api600Data.stem),
          // PACKING
          packing_material: getFieldValue(getDynParamValue('Gasket & Packing'), getDynParamValue('packing'), tcdsData.packing_material, tcdsData.Packing),
          // GASKET
          gasket_material: getFieldValue(getDynParamValue('Gasket & Packing'), getDynParamValue('gasket'), tcdsData.gasket_material, tcdsData.Gasket, api600Data.gasket),
          // STUD & NUT
          stud_nut_material: getFieldValue(getDynParamValue('Bolting'), getDynParamValue('stud_nut'), tcdsData.bolting_material, tcdsData['Stud & Nut'], api600Data.bolting),
          
            // Check valve - COVER
            body_material: getFieldValue(valveType === 'check' ? getDynParamValue('Body & Cover') : getDynParamValue('Body & Bonnet'), getDynParamValue('body'), tcdsData.body_material, tcdsData.Body, api600Data.body),
          // Check valve - COVER
          cover_material: getFieldValue(getDynParamValue('Body & Cover'), getDynParamValue('cover'), tcdsData.cover_material, tcdsData.Cover),
          // Check valve - PLUG
          plug_material: getFieldValue(getDynParamValue('Disc'), getDynParamValue('plug'), tcdsData.plug_material, tcdsData.Plug),
          // Check valve - HEX BOLT
          hex_bolt_material: getFieldValue(getDynParamValue('Bolting'), getDynParamValue('hex_bolt'), tcdsData.hex_bolt_material, tcdsData['Hex Bolt']),

            // Globe valve - SEAT RING
          seat_ring_material: getFieldValue(tcdsData.seat_ring_material, tcdsData['Seat Ring'], getDynParamValue('seat_ring'), api600Data.seat_ring),
          // Globe valve - GLAND PACKING
          gland_packing_material: getFieldValue(tcdsData.gland_packing_material, tcdsData['Gland Packing'], getDynParamValue('gland_packing')),
         
            // LSB fields - From TCDS / Database
         // LSB fields - From accessories_data parameters first, then TCDS top-level
          make: getFieldValue(getAccParamValue('MAKE'), tcdsData.make, getDynParamValue('make'), api600Data.make),
          model: getFieldValue(getAccParamValue('MODEL'), tcdsData.model, getDynParamValue('model')),
          enclosure: getFieldValue(getAccParamValue('ENCLOSURE MOC'), tcdsData.enclosure, getDynParamValue('enclosure')),
          serial_no: getFieldValue(tcdsData.serial_no, tcdsData.sr_no, getDynParamValue('serial_no')),
          // LSB Dropdown fields
          switch_type: getFieldValue(getAccParamValue('SWITCH/SENSOR TYPE'), tcdsData.switch_type, dynParams.switch_type),
          switch_make: getFieldValue(getAccParamValue('SWITCH/SENSOR MAKE'), tcdsData.switch_make, dynParams.switch_make),
          voltage: getFieldValue(getAccParamValue('SUPPLY VOLTAGE'), tcdsData.voltage, dynParams.voltage),
          protection: getFieldValue(getAccParamValue('AREA OF CLASSIFICATION'), tcdsData.protection, dynParams.protection),
          certification: getFieldValue(getAccParamValue('CERTIFICATE'), tcdsData.certification, dynParams.certification),
          format_no: getFieldValue(tcdsData.format_no, dynParams.format_no),
          // LSB Auto / Derived
          switch_model: getFieldValue(getAccParamValue('SWITCH/SENSOR Model'), tcdsData.switch_model, dynParams.switch_model),
          switch_qty: getFieldValue(getAccParamValue('SWITCH/SENSOR QTY'), tcdsData.switch_qty, dynParams.switch_qty),
          cable_entry: getFieldValue(getAccParamValue('CABLE ENTRY SIZE'), tcdsData.cable_entry, dynParams.cable_entry),
          cable_qty: getFieldValue(tcdsData.cable_qty, dynParams.cable_qty),
          
            
            // Ball valve fields
          valve_sr_no: getFieldValue(tcdsData.valve_sr_no, tcdsData.serial_no, dynParams.valve_sr_no),
          batch_no: getFieldValue(tcdsData.batch_no, dynParams.batch_no),
          description: getFieldValue(tcdsData.description, dynParams.description),
          valve_testing_std: getFieldValue(tcdsData.valve_testing_std, dynParams.valve_testing_std),
          type_of_test: getFieldValue(tcdsData.type_of_test, dynParams.type_of_test),
          test_results: getFieldValue(tcdsData.test_results, dynParams.test_results),
          test_pressure: getFieldValue(tcdsData.test_pressure, dynParams.test_pressure),
          test_duration: getFieldValue(tcdsData.test_duration, dynParams.test_duration),
          remark: getFieldValue(tcdsData.remark, dynParams.remark),
          min_test_pressure: getFieldValue(tcdsData.min_test_pressure, dynParams.min_test_pressure),
          min_test_duration: getFieldValue(tcdsData.min_test_duration, dynParams.min_test_duration),
          // Manual Entry defaults
          gad_no: getFieldValue(tcdsData.gad, tcdsData.gad_no),
          qap_no: getFieldValue(tcdsData.qap, tcdsData.qap_no),
          qty: getFieldValue(tcdsData.qty, tcdsData.quantity) || '1',
          sr_no: getFieldValue(tcdsData.sr_no, tcdsData.serial_no),
          heat_no: getFieldValue(tcdsData.heat_no, tcdsData.heatNumber),
          mtc_ref_no: getFieldValue(tcdsData.mtc_ref_no, tcdsData.mtcNumber),
          customer_item_code: getFieldValue(tcdsData.customer_item_code, tcdsData.customerItemCode),
          // Note: Only use 'remark' form field, not 'remarks' - they both map to database 'remarks' column
          // remark field is set above from TCDS data
          // System Generated defaults
          date: getFieldValue(tcdsData.date) || new Date().toISOString().split('T')[0],
        };
        
        // Apply direct mappings first - these are from TCDS and should always take priority
        console.log('[TCDS] Loading TCDS values - Size:', directMappings.size, 'Class:', directMappings.class);
        Object.keys(directMappings).forEach(key => {
          // Always apply TCDS-sourced values, overwriting any previous values
          if (directMappings[key]) {
            newFormData[key] = directMappings[key];
          }
        });
        console.log('[TCDS] After applying direct mappings - Size:', newFormData.size, 'Class:', newFormData.class);

        // Now fill in remaining fields from TCDS data using findValue
        allFields.forEach(field => {
          if (newFormData[field.id]) return;

          const value = findValue(field.source, tcdsData);
          if (value) {
            newFormData[field.id] = value;
          }
        });

        // Extract and populate accessories from TCDS accessories_data
        if (tcdsData.accessories_data) {
          try {
            const accessoriesArray = typeof tcdsData.accessories_data === 'string'
              ? JSON.parse(tcdsData.accessories_data)
              : tcdsData.accessories_data;

            if (Array.isArray(accessoriesArray)) {
              // Map accessory types to form field names
              const accessoryTypeMap = {
                'actuator': 'actuator',
                'positioner': 'positioner',
                'air filter regulator': 'air_filter_regulator',
                'solenoid valve': 'solenoid_valve',
                'limit switch box': 'limit_switch_box',
                'limit switch': 'limit_switch_box',
                'airlock relay': 'airlock_relay',
                'pilot valve': 'pilot_valve',
                'volume booster': 'volume_booster',
                'manual override': 'manual_override'
              };

              // Group accessories by type and format details
              accessoriesArray.forEach(acc => {
                if (!acc) return;

                const accType = (acc.accessoryName || acc.component || acc.type || '').toLowerCase().trim();
                const mappedFieldName = Object.keys(accessoryTypeMap).find(key => accType.includes(key));

                if (mappedFieldName) {
                  const fieldName = accessoryTypeMap[mappedFieldName];

                  // Skip if already populated
                  if (newFormData[fieldName]) return;

                  // Format accessory details
                  const details = [];
                  Object.keys(acc).forEach(key => {
                    if (key !== 'accessoryName' && key !== 'component' && key !== 'type' && acc[key]) {
                      details.push(`${key.replace(/_/g, ' ')}: ${acc[key]}`);
                    }
                  });

                  newFormData[fieldName] = details.length > 0 ? details.join(', ') : accType;
                }
              });
            }
          } catch (e) {
            console.warn("Error parsing accessories_data:", e);
          }
        }

        setFormData(newFormData);
        setView("form");

        // Handle tag numbers from TCDS for Automation/Standard valve type
        if (valveType === "automation") {
          // Extract tag_numbers array from TCDS
          let tcdsTagNumbers = [];
          try {
            if (tcdsData.tag_numbers) {
              tcdsTagNumbers = typeof tcdsData.tag_numbers === 'string'
                ? JSON.parse(tcdsData.tag_numbers)
                : Array.isArray(tcdsData.tag_numbers) ? tcdsData.tag_numbers : [];
            }
          } catch (e) {
            console.warn("Error parsing tag_numbers from TCDS:", e);
          }

          // Set tag numbers from TCDS
          setTagNumbersFromTCDS(tcdsTagNumbers);

          // Initialize Valve Details arrays based on quantity
          const qty = parseInt(newFormData.qty) || 0;
          const newIpcSrNoArray = Array(qty).fill("");
          const newValveBatchSrNoArray = Array(qty).fill("");

          setIpcSrNoArray(newIpcSrNoArray);
          setValveBatchSrNoArray(newValveBatchSrNoArray);
        }

        // Reset existing MTC tracking
        setExistingMtcId(null);
        setExistingMtcCertNo(null);

        // Check if an MTC was already generated for this TCDS + valve type
        try {
          const existingMtcResponse = await axios.get("/mtc", {
            params: { tcds_id: tcds.id, valve_type: valveType }
          });
          
          if (existingMtcResponse.data.success && existingMtcResponse.data.data && existingMtcResponse.data.data.length > 0) {
            const existingMtc = existingMtcResponse.data.data[0]; // Most recent MTC for this TCDS
            console.log("[MTC] Found existing MTC:", existingMtc.id, existingMtc.certificate_number);
            
            // Track the existing MTC ID and certificate number for updating later
            setExistingMtcId(existingMtc.id);
            setExistingMtcCertNo(existingMtc.certificate_number);

            // Parse item_description from existing MTC
            let savedItemDescription = {};
            if (existingMtc.item_description) {
              savedItemDescription = typeof existingMtc.item_description === 'string'
                ? JSON.parse(existingMtc.item_description)
                : existingMtc.item_description;
            }

            // Overlay saved MTC data on top of TCDS-populated fields
            // This restores all user-entered manual fields (heat numbers, MTC nos, QAP, GAD, remarks etc.)
            const mtcFieldOverrides = {
              certificate_no: existingMtc.certificate_number || '',
              tcds_no: existingMtc.tcds_no || newFormData.tcds_no,
              date: existingMtc.date ? new Date(existingMtc.date).toISOString().split('T')[0] : newFormData.date,
              customer: existingMtc.customer || newFormData.customer,
              project_name: existingMtc.project_name || newFormData.project_name,
              po_no: existingMtc.po_no || newFormData.po_no,
              po_date: existingMtc.po_date ? new Date(existingMtc.po_date).toISOString().split('T')[0] : newFormData.po_date,
              tag_no: existingMtc.tag_no || newFormData.tag_no,
              qap_no: existingMtc.qap_no || newFormData.qap_no,
              gad_no: existingMtc.gad_no || newFormData.gad_no,
              end_connection: existingMtc.end_connection || newFormData.end_connection,
              std_edition: existingMtc.std_edition || newFormData.std_edition,
              raised_face_std: existingMtc.raised_face_std || newFormData.raised_face_std,
              trim: existingMtc.trim || newFormData.trim,
              class: existingMtc.class || newFormData.class,
              qty: existingMtc.qty || newFormData.qty,
              size: existingMtc.size || newFormData.size,
              sr_no: existingMtc.sr_no || newFormData.sr_no,
              batch_no: existingMtc.batch_no || newFormData.batch_no,
              design_standard: existingMtc.design_standard || existingMtc.design_type || newFormData.design_standard,
              testing_standard: existingMtc.testing_std || existingMtc.testing_standard || newFormData.testing_standard,
              test_pressure: existingMtc.test_pressure || newFormData.test_pressure,
              testing_duration: existingMtc.testing_duration || newFormData.testing_duration,
              test_result: existingMtc.test_result || newFormData.test_result,
              hydro_back_seat: existingMtc.hydro_back_seat || newFormData.hydro_back_seat,
              hydro_body: existingMtc.hydro_body || newFormData.hydro_body,
              hydro_seat: existingMtc.hydro_seat || newFormData.hydro_seat,
              pneumatic_seat: existingMtc.pneumatic_seat || newFormData.pneumatic_seat,
              material_spec: existingMtc.material_spec || newFormData.material_spec,
              heat_no: existingMtc.heat_no || newFormData.heat_no,
              mtc_ref_no: existingMtc.mtc_ref_no || newFormData.mtc_ref_no,
              remark: existingMtc.remarks || existingMtc.remark || newFormData.remark,
              conformity_statement: existingMtc.conformity_statement || newFormData.conformity_statement,
              customer_item_code: existingMtc.customer_item_code || newFormData.customer_item_code,
              prepared_by: existingMtc.prepared_by || newFormData.prepared_by,
              prepared_by_name: existingMtc.prepared_by_name || newFormData.prepared_by_name,
              approved_by: existingMtc.approved_by || newFormData.approved_by,
              qac_officer: existingMtc.qac_officer || newFormData.qac_officer,
              // Automation/Manual entry fields
              oa_no: existingMtc.oa_no || newFormData.oa_no,
              oa_date: existingMtc.oa_date ? new Date(existingMtc.oa_date).toISOString().split('T')[0] : newFormData.oa_date,
              ga_drawing_no: existingMtc.ga_drawing_no || newFormData.ga_drawing_no,
              fail_position: existingMtc.fail_position || newFormData.fail_position,
              power_fail_position: existingMtc.power_fail_position || newFormData.power_fail_position,
              ipc_sr_no: existingMtc.ipc_sr_no || newFormData.ipc_sr_no,
              valve_batch_sr_no: existingMtc.valve_batch_sr_no || newFormData.valve_batch_sr_no,
              observation: existingMtc.observation || newFormData.observation,
              remark: existingMtc.remarks || existingMtc.remark || newFormData.remark,
              declaration_of_compliance: existingMtc.declaration_of_compliance || newFormData.declaration_of_compliance,
              min_air_supply: existingMtc.min_air_supply || newFormData.min_air_supply,
              pressure_rating: existingMtc.pressure_rating || newFormData.pressure_rating,
              body_hydrostatic: existingMtc.body_hydrostatic || newFormData.body_hydrostatic,
              seat_hydrostatic: existingMtc.seat_hydrostatic || newFormData.seat_hydrostatic,
              seat_pneumatic: existingMtc.seat_pneumatic || newFormData.seat_pneumatic,
              test_duration: existingMtc.test_duration || newFormData.test_duration,
            };

            // Apply MTC overrides - only override if value is non-empty
            // BUT: Keep TCDS-sourced fields from TCDS (don't overwrite with old MTC values)
            // These fields should always be refreshed from current TCDS data
            const fieldsToKeepFromTCDS = [
              'size', 'class', 'end_connection',
              'trim', 'make', 'series', 'type', 'operator', 'painting_details',
              'flow_direction', 'operation', 'body_material', 'bonnet_material',
              'disc_material', 'stem_material', 'seat_material', 'gasket_material',
              'packing_material', 'bolting_material', 'customer_item_code',
              'tag_no', 'qty', 'ipc_sr_no', 'valve_batch_sr_no', 'min_air_supply',
              'customer', 'po_no', 'po_date'
            ];

            console.log('[MTC] Before applying overrides - Size:', newFormData.size, 'Class:', newFormData.class);

            Object.keys(mtcFieldOverrides).forEach(key => {
              // Skip TCDS-sourced fields - keep current TCDS values instead of old MTC values
              if (fieldsToKeepFromTCDS.includes(key)) {
                console.log(`[MTC] Skipping override for TCDS field: ${key} (keeping TCDS value: ${newFormData[key]})`);
                return; // Skip this field, keep the fresh TCDS value from newFormData
              }

              const val = mtcFieldOverrides[key];
              if (val !== undefined && val !== null && String(val).trim() !== '') {
                newFormData[key] = val;
              }
            });

            console.log('[MTC] After applying overrides - Size:', newFormData.size, 'Class:', newFormData.class);

            // Restore saved item_description fields (material heat nos, MTC nos, etc.)
            Object.keys(savedItemDescription).forEach(key => {
              if (savedItemDescription[key]) {
                newFormData[key] = savedItemDescription[key];
              }
            });

            // Restore material fields from top-level MTC columns
            // (ONLY if they're not in the fieldsToKeepFromTCDS list)
            if (existingMtc.body_material) newFormData.body_material = existingMtc.body_material;
            if (existingMtc.disc_material) newFormData.wedge_material = existingMtc.disc_material;
            if (existingMtc.stem_material) newFormData.stem_material = existingMtc.stem_material;
            if (existingMtc.seat_material) newFormData.seatring_material = existingMtc.seat_material;
            if (existingMtc.bonnet_material) newFormData.bonnet_material = existingMtc.bonnet_material;
            if (existingMtc.gasket_material) newFormData.gasket_material = existingMtc.gasket_material;
            if (existingMtc.packing_material) newFormData.packing_material = existingMtc.packing_material;
            if (existingMtc.bolting_material) newFormData.stud_nut_material = existingMtc.bolting_material;

            // Update formData with the restored values
            setFormData({ ...newFormData });

            // Restore heat annexure data if it was attached
            if (existingMtc.attach_heat_annexure && existingMtc.heat_annexure_data) {
              const savedHeatData = typeof existingMtc.heat_annexure_data === 'string'
                ? JSON.parse(existingMtc.heat_annexure_data)
                : existingMtc.heat_annexure_data;

              if (Array.isArray(savedHeatData) && savedHeatData.length > 0) {
                setAttachHeatAnnexure(true);
                setHeatAnnexureData(savedHeatData);
                // Restore selected components from heat data
                const savedComponents = [...new Set(savedHeatData.map(row => row.component).filter(Boolean))];
                setSelectedHeatComponents(savedComponents);
                setHeatAnnexureQuantity(existingMtc.qty || 1);
              }
            }

            // Restore Valve Details arrays for Automation MTC
            if (valveType === "automation") {
              let ipcSrArray = [];
              let valveBatchSrArray = [];

              // Parse ipc_sr_no array if it exists
              if (existingMtc.ipc_sr_no) {
                try {
                  ipcSrArray = Array.isArray(existingMtc.ipc_sr_no)
                    ? existingMtc.ipc_sr_no
                    : typeof existingMtc.ipc_sr_no === 'string'
                      ? JSON.parse(existingMtc.ipc_sr_no)
                      : [existingMtc.ipc_sr_no];
                } catch (e) {
                  ipcSrArray = existingMtc.ipc_sr_no ? [existingMtc.ipc_sr_no] : [];
                }
              }

              // Parse valve_batch_sr_no array if it exists
              if (existingMtc.valve_batch_sr_no) {
                try {
                  valveBatchSrArray = Array.isArray(existingMtc.valve_batch_sr_no)
                    ? existingMtc.valve_batch_sr_no
                    : typeof existingMtc.valve_batch_sr_no === 'string'
                      ? JSON.parse(existingMtc.valve_batch_sr_no)
                      : [existingMtc.valve_batch_sr_no];
                } catch (e) {
                  valveBatchSrArray = existingMtc.valve_batch_sr_no ? [existingMtc.valve_batch_sr_no] : [];
                }
              }

              setIpcSrNoArray(ipcSrArray);
              setValveBatchSrNoArray(valveBatchSrArray);
            }

            showNotification("success", `Loaded existing MTC certificate: ${existingMtc.certificate_number}`);
          }
        } catch (mtcLookupError) {
          console.warn("[MTC] Could not check for existing MTC:", mtcLookupError);
          // Non-fatal: continue with TCDS data only
        }
        
        // After loading TCDS, auto-fetch testing details if size, class, and testing_std are available
        const size = newFormData.size;
        const classVal = newFormData.class;
        // Handle both testing_std, testing_standard, and valve_testing_std field names
        // valve_testing_std is used for Ball valve MTC
        let testingStd = newFormData.testing_standard || newFormData.testing_std || newFormData.valve_testing_std;

        // If testing_std exists but testing_standard doesn't, populate testing_standard
        if (newFormData.testing_std && !newFormData.testing_standard) {
          newFormData.testing_standard = newFormData.testing_std;
          testingStd = newFormData.testing_std;
        }

        if (size && classVal && testingStd) {
          // Small delay to ensure formData is set before fetching
          setTimeout(() => fetchTestingDetails(size, classVal, testingStd), 100);
        }
      }
    } catch (error) {
      console.error("Failed to fetch TCDS details:", error);
      showNotification("error", "Failed to load TCDS details");
    } finally {
      setFetchingTCDS(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for quantity field
    if (name === "qty") {
      const qty = parseInt(value) || 0;
      
      // Validate quantity is a positive number
      if (qty < 0) {
        showNotification("error", "Quantity must be a positive number");
        return;
      }
      
      // Update quantity in formData
      setFormData(prev => ({ ...prev, qty: qty }));
      
      // Dynamically create tag number array
      const newTagNumbers = [];
      for (let i = 0; i < qty; i++) {
        newTagNumbers.push(tagNumbers[i] || ""); // Preserve existing values or empty string
      }
      setTagNumbers(newTagNumbers);

      // Also resize Valve Details arrays for Automation MTC
      if (valveType === "automation") {
        const newIpcSrNoArray = [];
        const newValveBatchSrNoArray = [];
        for (let i = 0; i < qty; i++) {
          newIpcSrNoArray.push(ipcSrNoArray[i] || "");
          newValveBatchSrNoArray.push(valveBatchSrNoArray[i] || "");
        }
        setIpcSrNoArray(newIpcSrNoArray);
        setValveBatchSrNoArray(newValveBatchSrNoArray);
      }
    } else {
      setFormData(prev => {
        const updated = { ...prev, [name]: value };
        
        // When SIZE, CLASS, or TESTING STD changes, auto-fetch testing details
        if (name === "size" || name === "class" || name === "testing_std" || name === "testing_standard" || name === "valve_testing_std") {
          const size = name === "size" ? value : prev.size;
          const classVal = name === "class" ? value : prev.class;
          // Handle both testing_std, testing_standard, and valve_testing_std field names
          const testingStd = name === "testing_std" ? value : (name === "testing_standard" ? value : (name === "valve_testing_std" ? value : (prev.testing_std || prev.testing_standard || prev.valve_testing_std)));

          // Fetch testing parameters if all three values are present
          if (size && classVal && testingStd) {
            fetchTestingDetails(size, classVal, testingStd);
          }
        }
        
        // When SR_NO changes for GGC valves, regenerate heat annexure rows with new serial numbers
        if (name === "sr_no" && ['gate', 'globe', 'check'].includes(valveType) && attachHeatAnnexure) {
          // Small delay to ensure formData is updated
          setTimeout(() => {
            generateHeatAnnexureRows(heatAnnexureQuantity, selectedHeatComponents);
          }, 50);
        }
        
        return updated;
      });
    }
  };
  
  const handleTagNumberChange = (index, value) => {
    const newTagNumbers = [...tagNumbers];
    newTagNumbers[index] = value;
    setTagNumbers(newTagNumbers);
  };

  const handleIpcSrNoChange = (index, value) => {
    const newArray = [...ipcSrNoArray];
    newArray[index] = value;
    setIpcSrNoArray(newArray);
  };

  const handleValveBatchSrNoChange = (index, value) => {
    const newArray = [...valveBatchSrNoArray];
    newArray[index] = value;
    setValveBatchSrNoArray(newArray);
  };

  const validateRequiredFields = () => {
    const missingFields = [];

    // Check all fields (formFields + materialFields + materialManualFields)
    allFields.forEach(field => {
      if (field.required && (!formData[field.id] || String(formData[field.id]).trim() === "")) {
        missingFields.push(field.label);
      }
    });

    return missingFields;
  };

  const saveMTCData = async () => {
    // Prepare item description data from materialFields
    const itemDescription = {};
    (config.materialFields || []).forEach(field => {
      if (formData[field.id]) {
        itemDescription[field.id] = formData[field.id];
      }
    });
    (config.materialManualFields || []).forEach(field => {
      if (formData[field.id]) {
        itemDescription[field.id] = formData[field.id];
      }
    });

    // Map item description fields to top-level material fields for ViewCertificate compatibility
    const materialFieldMappings = {
      body: 'body_material',
      Body: 'body_material',
      bonnet: 'bonnet_material',
      Bonnet: 'bonnet_material',
      wedge: 'disc_material',
      Wedge: 'disc_material',
      disc: 'disc_material',
      Disk: 'disc_material',
      seat_ring: 'seat_material',
      'Seat Ring': 'seat_material',
      seat: 'seat_material',
      Seat: 'seat_material',
      stem: 'stem_material',
      Stem: 'stem_material',
      gasket: 'gasket_material',
      Gasket: 'gasket_material',
      packing: 'packing_material',
      Packing: 'packing_material',
      bolting: 'bolting_material',
      Bolting: 'bolting_material',
      'stud_nut': 'bolting_material',
      'Stud & Nut': 'bolting_material',
    };

    const topLevelMaterials = {};
    Object.entries(materialFieldMappings).forEach(([itemKey, dbField]) => {
      if (itemDescription[itemKey] && !topLevelMaterials[dbField]) {
        topLevelMaterials[dbField] = itemDescription[itemKey];
      }
    });

    // Also include individual heat_no and mtc_no fields for each material
    const materialHeatMtcFields = {};
    (config.materialFields || []).forEach(field => {
      if (field.id && (field.id.endsWith('_heat_no') || field.id.endsWith('_mtc_no') || field.id.endsWith('_material'))) {
        if (formData[field.id]) {
          materialHeatMtcFields[field.id] = formData[field.id];
        }
      }
    });

    // Get unit from TCDS data (unit_id field) - convert to U1/U2 format
    let unitValue = 'U1'; // Default to U1
    if (selectedTCDSData?.unit_id) {
      const unitId = selectedTCDSData.unit_id;
      // Match with unitOptions if available
      const matchedUnit = unitOptions.find(u => u.id === unitId);
      if (matchedUnit) {
        unitValue = matchedUnit.value;
      } else {
        // Fallback: unit_id 1 = U1, 2 = U2
        unitValue = unitId === 1 ? 'U1' : unitId === 2 ? 'U2' : `U${unitId}`;
      }
    }

    // Prepare MTC data for API
    const mtcData = {
      certificate_number: existingMtcCertNo || null, // Use existing cert no for updates, auto-generate for new
      unit: unitValue, // From TCDS data
      tcds_id: selectedTCDS?.id || null,
      tcds_no: formData.tcds_no,
      tcds_date: formData.tcds_date,
      valve_type: valveType,
      date: formData.date,
      customer: formData.customer,
      project_name: formData.project_name,
      po_no: formData.po_no,
      po_date: formData.po_date,
      gad_no: formData.gad_no,
      tag_no: formData.tag_no,
      qap_no: formData.qap_no,
      end_connection: formData.end_connection,
      std_edition: formData.std_edition,
      raised_face_std: formData.raised_face_std,
      trim: formData.trim,
      class: formData.class,
      qty: formData.qty ? parseInt(formData.qty) : 1,
      size: formData.size,
      sr_no: formData.sr_no,
      batch_no: formData.batch_no || formData.certificate_no,
      design_standard: formData.design_standard,
      testing_std: formData.testing_std || formData.testing_standard,
      test_pressure: formData.test_pressure,
      testing_duration: formData.testing_duration,
      test_result: formData.test_result,
      hydro_back_seat: formData.hydro_back_seat,
      hydro_body: formData.hydro_body,
      hydro_seat: formData.hydro_seat,
      pneumatic_seat: formData.pneumatic_seat,
      material_spec: formData.material_spec,
      heat_no: formData.heat_no,
      mtc_ref_no: formData.mtc_ref_no,
      remarks: formData.remark || formData.remarks, // Map form field 'remark' to database column 'remarks'
      conformity_statement: formData.conformity_statement,
      customer_item_code: formData.customer_item_code,
      prepared_by: formData.prepared_by,
      approved_by: formData.approved_by,
      prepared_by_name: formData.prepared_by_name,
      qac_officer: formData.qac_officer,
      // Automation certificate fields
      declaration_of_compliance: formData.declaration_of_compliance,
      oa_no: formData.oa_no,
      oa_date: formData.oa_date,
      ga_drawing_no: formData.ga_drawing_no,
      fail_position: formData.fail_position,
      power_fail_position: formData.power_fail_position,
      ipc_sr_no: valveType === "automation" && ipcSrNoArray.length > 0
  ? JSON.stringify(ipcSrNoArray.filter(v => v.trim() !== ""))
  : (formData.ipc_sr_no ? JSON.stringify([formData.ipc_sr_no]) : null),

valve_batch_sr_no: valveType === "automation" && valveBatchSrNoArray.length > 0
  ? JSON.stringify(valveBatchSrNoArray.filter(v => v.trim() !== ""))
  : (formData.valve_batch_sr_no ? JSON.stringify([formData.valve_batch_sr_no]) : null),
      testing_standard: formData.testing_standard || formData.testing_std,
      min_air_supply: formData.min_air_supply,
      pressure_rating: formData.pressure_rating,
      body_hydrostatic: formData.body_hydrostatic,
      seat_hydrostatic: formData.seat_hydrostatic,
      seat_pneumatic: formData.seat_pneumatic,
      test_duration: formData.test_duration,
      observation: formData.observation,
      ...topLevelMaterials,
      ...materialHeatMtcFields, // Include individual heat_no and mtc_no for each material
      item_description: itemDescription,
      status: 'generated',
      // Heat Annexure data
      attach_heat_annexure: attachHeatAnnexure,
      heat_annexure_data: attachHeatAnnexure ? heatAnnexureData : null
    };

    let response;
    if (existingMtcId) {
      // Update existing MTC
      console.log("[MTC] Updating existing MTC:", existingMtcId);
      response = await axios.put(`/mtc/${existingMtcId}`, mtcData);
      // Ensure the response includes the certificate number
      if (response.data && response.data.data) {
        response.data.data.certificate_no = existingMtcCertNo;
        response.data.data.certificate_number = existingMtcCertNo;
      }
    } else {
      // Create new MTC
      console.log("[MTC] Creating new MTC");
      response = await axios.post('/mtc', mtcData);
      // Track the newly created MTC so subsequent saves will update it
      if (response.data?.data?.id) {
        setExistingMtcId(response.data.data.id);
        setExistingMtcCertNo(response.data.data.certificate_no || response.data.data.certificate_number);
      }
    }
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      showNotification("error", `Please fill in required fields: ${missingFields.slice(0, 3).join(", ")}${missingFields.length > 3 ? ` and ${missingFields.length - 3} more` : ""}`);
      return;
    }

    try {
      setLoading(true);

      // Save MTC data to backend
      const result = await saveMTCData();

      // Update certificate_no in form with auto-generated value
      if (result.data?.certificate_no) {
        setFormData(prev => ({
          ...prev,
          certificate_no: result.data.certificate_no
        }));
      }

      showNotification("success", `MTC Certificate saved successfully (${result.data?.certificate_no || 'ID: ' + result.data?.id})`);

      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error) {
      console.error("Failed to save MTC:", error);
      showNotification("error", error.response?.data?.message || "Failed to save MTC");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setView("list");
    setSelectedTCDS(null);
    setSelectedTCDSData(null);
    setExistingMtcId(null);
    setExistingMtcCertNo(null);
  };

  const handleDownloadPdf = async () => {
    // ── Guard against duplicate clicks / re-renders ──────────────────────────
    if (downloadLockRef.current || isDownloading) {
      console.warn('[PDF] Download already in progress – ignoring duplicate click.');
      return;
    }
    downloadLockRef.current = true;
    setIsDownloading(true);

    // Validate required fields
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      showNotification("error", `Please fill in required fields: ${missingFields.slice(0, 3).join(", ")}${missingFields.length > 3 ? ` and ${missingFields.length - 3} more` : ""}`);
      downloadLockRef.current = false;
      setIsDownloading(false);
      return;
    }

    try {
      // Save MTC data first
      const result = await saveMTCData();

      // Determine the certificate number (from existing MTC or newly created)
      const certNo = existingMtcCertNo || result.data?.certificate_no || result.data?.certificate_number || formData.certificate_no;

      // Update certificate_no in form
      if (certNo && certNo !== formData.certificate_no) {
        setFormData(prev => ({
          ...prev,
          certificate_no: certNo
        }));
      }

      // Use stored TCDS data if available, otherwise use empty object
      const tcdsData = selectedTCDSData || {};

      // Generate PDF with form data, TCDS data, and heat annexure data if attached
      const heatData = attachHeatAnnexure ? heatAnnexureData : null;

      // Fetch attachment images AND PDFs from heat annexure configurations
      let attachmentImages = [];
      let attachmentPdfs = [];
      if (attachHeatAnnexure && heatData && heatData.length > 0) {
        try {
          // Get unique attachment IDs from heat annexure data
          const attachmentIds = [...new Set(heatData.map(row => row.attachmentId).filter(id => id))];
          console.log('[PDF] Found attachment IDs:', attachmentIds);

          for (const attachmentId of attachmentIds) {
            try {
              const response = await axios.get(`/heat-annexure/${attachmentId}/attachment`, {
                responseType: 'blob',
                timeout: 30000
              });
              if (response.data && response.data.size > 0) {
                const mimeType = response.data.type || 'image/jpeg';

                if (mimeType === 'application/pdf') {
                  const base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(response.data);
                  });
                  attachmentPdfs.push({ url: base64, mimetype: mimeType });
                } else {
                  const base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result.split(',')[1]);
                    reader.onerror = reject;
                    reader.readAsDataURL(response.data);
                  });
                  attachmentImages.push({
                    url: `data:${mimeType};base64,${base64}`,
                    label: `Attachment: ${attachmentId}`
                  });
                }
              }
            } catch (imgErr) {
              console.warn('[PDF] Failed to fetch attachment ID:', attachmentId, imgErr.message);
            }
          }
        } catch (err) {
          console.warn('[PDF] Failed to fetch attachments:', err.message);
        }
      }
      console.log('[PDF] Image attachments:', attachmentImages.length, '| PDF attachments:', attachmentPdfs.length);

      // Prepare form data for PDF – ensure certificate_no is up-to-date
      const pdfFormData = { ...formData };
      if (certNo) {
        pdfFormData.certificate_no = certNo;
      }
      pdfFormData.design_standard = result.data?.design_standard || result.data?.design_type || pdfFormData.design_standard;
      pdfFormData.testing_std = result.data?.testing_std || result.data?.testing_standard || pdfFormData.testing_std;
            
       // Pass the actual arrays to PDF generator
pdfFormData.ipc_sr_no_array = ipcSrNoArray.filter(v => v && String(v).trim() !== "");
pdfFormData.valve_batch_sr_no_array = valveBatchSrNoArray.filter(v => v && String(v).trim() !== "");
pdfFormData.tag_numbers_array = tagNumbersFromTCDS || [];

console.log("[PDF DEBUG] ipcSrNoArray state:", ipcSrNoArray);
console.log("[PDF DEBUG] valveBatchSrNoArray state:", valveBatchSrNoArray);
console.log("[PDF DEBUG] pdfFormData.ipc_sr_no_array:", pdfFormData.ipc_sr_no_array);
console.log("[PDF DEBUG] pdfFormData.valve_batch_sr_no_array:", pdfFormData.valve_batch_sr_no_array);


      // ── Generate ONE PDF for the selected valve type only ────────────────────
      // Note: We intentionally do NOT auto-generate a second Automation PDF here.
      // If the user needs an Automation certificate they should switch to the
      // Automation MTC card. Generating two PDFs on one click was causing the
      // "multiple downloads" bug.
      await generateMTCPdf(tcdsData, pdfFormData, valveType, heatData, attachmentImages, attachmentPdfs);

      showNotification("success", "PDF generated and downloaded successfully");
    } catch (error) {
      console.error('[PDF] Error generating PDF:', error);

      // Build a user-friendly error message from whatever the backend returned
      let errMsg = 'Failed to generate PDF';
      if (error.response) {
        const body = error.response.data;
        if (typeof body === 'string' && body.length < 300) {
          errMsg = body;
        } else if (body?.message) {
          errMsg = body.message;
        } else if (body?.error) {
          errMsg = body.error;
        } else {
          errMsg = `Server error ${error.response.status}: Failed to generate PDF`;
        }
      } else if (error.message) {
        errMsg = error.message;
      }
      showNotification('error', errMsg);
    } finally {
      // Always release the lock so the button becomes clickable again
      downloadLockRef.current = false;
      setIsDownloading(false);
    }
  };

  const getRecordsByType = (type) => {
    return tcdsList.filter((record) => record.tcds_type === type);
  };

  // Render TCDS List View
  if (view === "list") {
    return (
      <div className="gate-mtc-container">
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}

        {notification.show && (
          <div className={`notification ${notification.type}`}>
            <div className="notification-icon">
              {notification.type === "success" ? "✓" : "✕"}
            </div>
            <div className="notification-text">{notification.message}</div>
          </div>
        )}

        <div className="gate-mtc-header">
          <button className="mtc-back-button" onClick={onBack}>
            ← Back to MTC
          </button>
          <h1 className="gate-mtc-title">{config.title}</h1>
          <p className="gate-mtc-subtitle">
            Select a TCDS to create MTC Certificate
          </p>
        </div>

        <div className="tcds-list-section">
          <h2 className="section-title">Available TCDS Records for {valveType.charAt(0).toUpperCase() + valveType.slice(1)} MTC</h2>
          
          {tcdsList.length === 0 ? (
            <div className="no-tcds-message">
              <p>No matching TCDS records found for this MTC type.</p>
              <p>Please create a TCDS first from the Sales module.</p>
            </div>
          ) : (
            <div className="tcds-source-groups">
              {/* Group records by tcds_type */}
              {["Automation/Standard", "API 600", "API 6D"].map((sourceType) => {
                const records = getRecordsByType(sourceType);
                if (records.length === 0) return null; // Don't show empty groups

                return (
                  <div className="tcds-source-group" key={sourceType}>
                    <h3 className="section-title" style={{ marginTop: "10px" }}>{sourceType}</h3>

                    <div className="tcds-grid">
                      {records.map((tcds) => (
                        <div
                          key={tcds.id}
                          className="tcds-card"
                          onClick={() => handleTCDSSelect(tcds)}
                        >
                          <div className="tcds-card-header">
                            <span className="tcds-number">{tcds.section_number || tcds.id}</span>
                            <span className={`tcds-status ${tcds.status}`}>{tcds.status}</span>
                          </div>
                          <div className="tcds-card-body">
                            <p className="tcds-customer">{tcds.customer_name || "N/A"}</p>
                            <p className="tcds-project">{tcds.project_name || "N/A"}</p>
                            <p className="tcds-po">PO: {tcds.purchase_order_no || "N/A"}</p>
                            {tcds.type && <p className="tcds-valve-type">Valve: {tcds.type}</p>}
                          </div>
                          <div className="tcds-card-footer">
                            <span className="tcds-type">{tcds.tcds_type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render MTC Form View
  return (
    <div className="gate-mtc-container">
      {fetchingTCDS && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-icon">
            {notification.type === "success" ? "✓" : "✕"}
          </div>
          <div className="notification-text">{notification.message}</div>
        </div>
      )}

      <div className="gate-mtc-header">
        <button className="mtc-back-button" onClick={handleBackToList}>
          ← Back to TCDS List
        </button>
        <h1 className="gate-mtc-title">{config.subtitle}</h1>
        <p className="gate-mtc-subtitle">
          {selectedTCDS ? `TCDS No: ${selectedTCDS.section_number || selectedTCDS.id}` : ""}
        </p>
      </div>

      <form className="gate-mtc-form" onSubmit={handleSubmit}>
        {/* Render form fields grouped by section */}
        {(() => {
          const formFields = config.formFields || [];
          const materialFields = config.materialFields || [];
          const materialManualFields = config.materialManualFields || [];
          
          // Group fields by section
          const sections = {};
          formFields.forEach(field => {
            const section = field.section || 'other';
            if (!sections[section]) sections[section] = [];
            sections[section].push(field);
          });
          
          // Section titles mapping
          const sectionTitles = {
            header: 'Header Information',
            details: 'Valve Details',
            product: 'Product Details',
            standards: 'Applicable Standards',
            testing: 'Testing Details',
            remark: 'Remarks',
            conformity: 'Statement of Conformity',
            signature: 'Signatures',
            declaration: 'Declaration',
            automation_valve: 'Valve Details & Testing Pressure',
            other: 'Other'
          };
          
          return (
            <>
              {/* Render grouped form fields */}
              {Object.keys(sections).map(section => {
                // Only show automation_valve section if a valve is selected in TCDS
                if (section === "automation_valve" && valveType === "automation" && !hasValveInTCDS) {
                  return null;
                }
                return (
                  <div key={section} className="mtc-form-section">
                    <div className="section-header">
                      <h3>{sectionTitles[section] || section}</h3>
                    </div>
                    <div className="form-grid">
                      {sections[section].map(field => (
                        <FormField
                          key={field.id}
                          field={field}
                          value={formData[field.id]}
                          onChange={handleInputChange}
                          qty={formData.qty ? parseInt(formData.qty) : 0}
                          tagNumbers={tagNumbers}
                          onTagNumberChange={handleTagNumberChange}
                          ipcSrNoArray={ipcSrNoArray}
                          valveBatchSrNoArray={valveBatchSrNoArray}
                          tagNumbersFromTCDS={tagNumbersFromTCDS}
                          onIpcSrNoChange={handleIpcSrNoChange}
                          onValveBatchSrNoChange={handleValveBatchSrNoChange}
                          dynamicOptions={{
                            units: unitOptions,
                            reviewers: reviewerOptions,
                            declarations: declarationOptions,
                            testingStandards: testingStandardOptions
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {/* Material/Components Section */}
              {materialFields.length > 0 && (
                <div className="mtc-form-section">
                  <div className="section-header">
                    <h3>{valveType === 'ball' ? 'Material of Construction' : 
                         valveType === 'actuator' ? 'Material Compliance' :
                         valveType === 'lsb' ? 'LSB Details' : 'Material Table'}</h3>
                  </div>
                  
                  {/* Check if material fields have materialItem property (Gate/Check/Globe valve format) */}
                  {materialFields.some(f => f.materialItem) ? (
                    // Render as a proper table with grouped rows
                    <div className="material-table-container">
                      <table className="material-table">
                        <thead>
                          <tr>
                            <th style={{ width: '20%' }}>ITEM DESCRIPTION</th>
                            <th style={{ width: '30%' }}>MATERIAL SPECIFICATION</th>
                            <th style={{ width: '25%' }}>HEAT NO. / ID NO. / LOT NO.</th>
                            <th style={{ width: '25%' }}>MTC NO.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Group fields by materialItem
                            const items = {};
                            materialFields.forEach(field => {
                              if (field.materialItem) {
                                if (!items[field.materialItem]) items[field.materialItem] = {};
                                if (field.id.endsWith('_material')) {
                                  items[field.materialItem].material = field;
                                } else if (field.id.endsWith('_heat_no')) {
                                  items[field.materialItem].heat_no = field;
                                } else if (field.id.endsWith('_mtc_no')) {
                                  items[field.materialItem].mtc_no = field;
                                }
                              }
                            });
                            
                            return Object.keys(items).map(itemName => (
                              <tr key={itemName}>
                                <td className="item-name">{itemName}</td>
                                <td>
                                  <input
                                    type="text"
                                    name={items[itemName].material?.id}
                                    value={formData[items[itemName].material?.id] || ""}
                                    onChange={handleInputChange}
                                    className={formData[items[itemName].material?.id] ? "has-value" : ""}
                                    readOnly={items[itemName].material?.inputType === 'tcds'}
                                    placeholder="From TCDS"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    name={items[itemName].heat_no?.id}
                                    value={attachHeatAnnexure ? "Refer Annexure" : (formData[items[itemName].heat_no?.id] || "")}
                                    onChange={handleInputChange}
                                    placeholder="Enter Heat No."
                                    className={attachHeatAnnexure ? "has-value refer-annexure" : "manual-input"}
                                    readOnly={attachHeatAnnexure}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    name={items[itemName].mtc_no?.id}
                                    value={formData[items[itemName].mtc_no?.id] || ""}
                                    onChange={handleInputChange}
                                    placeholder="Enter MTC No."
                                    className="manual-input"
                                  />
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    // Regular grid format for Ball/Actuator/LSB
                    <div className="form-grid">
                      {materialFields.map(field => (
                        <FormField
                          key={field.id}
                          field={field}
                          value={formData[field.id]}
                          onChange={handleInputChange}
                          qty={formData.qty ? parseInt(formData.qty) : 0}
                          tagNumbers={tagNumbers}
                          onTagNumberChange={handleTagNumberChange}
                          ipcSrNoArray={ipcSrNoArray}
                          valveBatchSrNoArray={valveBatchSrNoArray}
                          tagNumbersFromTCDS={tagNumbersFromTCDS}
                          onIpcSrNoChange={handleIpcSrNoChange}
                          onValveBatchSrNoChange={handleValveBatchSrNoChange}
                          dynamicOptions={{
                            units: unitOptions,
                            reviewers: reviewerOptions,
                            declarations: declarationOptions,
                            testingStandards: testingStandardOptions
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Material Manual Entry Fields - only if there are separate materialManualFields */}
              {materialManualFields.length > 0 && (
                <div className="mtc-form-section">
                  <div className="section-header">
                    <h3>Material Manual Entry</h3>
                  </div>
                  <div className="form-grid">
                    {materialManualFields.map(field => (
                      <FormField
                        key={field.id}
                        field={field}
                        value={formData[field.id]}
                        onChange={handleInputChange}
                        qty={formData.qty ? parseInt(formData.qty) : 0}
                        ipcSrNoArray={ipcSrNoArray}
                        valveBatchSrNoArray={valveBatchSrNoArray}
                        tagNumbersFromTCDS={tagNumbersFromTCDS}
                        onIpcSrNoChange={handleIpcSrNoChange}
                        onValveBatchSrNoChange={handleValveBatchSrNoChange}
                        dynamicOptions={{ units: unitOptions, reviewers: reviewerOptions }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Heat Annexure Section - For Ball valve and GGC (Gate, Globe, Check) valves */}
              {(valveType === 'ball' || valveType === 'gate' || valveType === 'globe' || valveType === 'check') && (
                <div className="mtc-form-section heat-annexure-section">
                  <div className="section-header">
                    <h3>Heat Annexure</h3>
                  </div>
                  
                  {/* Checkbox to toggle Heat Annexure */}
                  <div className="heat-annexure-toggle">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={attachHeatAnnexure}
                        onChange={(e) => {
                          setAttachHeatAnnexure(e.target.checked);
                          if (!e.target.checked) {
                            // Reset heat annexure data when unchecked
                            setSelectedHeatComponents([]);
                            setHeatAnnexureQuantity(1);
                            setHeatAnnexureData([]);
                          }
                        }}
                      />
                      <span>Attach Heat Annexure</span>
                    </label>
                    {attachHeatAnnexure && (
                      <p className="heat-annexure-note">
                        Heat number fields in the certificate will show "Refer Annexure"
                      </p>
                    )}
                  </div>

                  {/* Heat Annexure Form - Visible when checkbox is checked */}
                  {attachHeatAnnexure && (
                    <div className="heat-annexure-form">
                      <div className="heat-annexure-inputs">
                        {/* Select Components - Multi-select with checkboxes */}
                        <div className="form-field">
                          <label>Select Components</label>
                          <div className="component-checkboxes">
                            {heatAnnexureComponents.map(comp => (
                              <label key={comp.id || comp.value} className="component-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedHeatComponents.includes(comp.value || comp.name)}
                                  onChange={(e) => handleComponentSelectionChange(
                                    comp.value || comp.name, 
                                    e.target.checked
                                  )}
                                />
                                <span>{comp.value || comp.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Batch Number (Ball) or Sr. Number (GGC) - Auto-filled */}
                        <div className="form-field">
                          <label>{valveType === 'ball' ? 'Batch No.' : 'Sr. Number'}</label>
                          <input
                            type="text"
                            value={valveType === 'ball' 
                              ? (formData.batch_no || formData.batchNo || "")
                              : (formData.sr_no || formData.srNo || formData.section_number || "")}
                            readOnly
                            className="has-value"
                          />
                        </div>

                        {/* Quantity */}
                        <div className="form-field">
                          <label>Enter Quantity</label>
                          <input
                            type="number"
                            min="1"
                            value={heatAnnexureQuantity}
                            onChange={(e) => handleHeatAnnexureQuantityChange(e.target.value)}
                            className="manual-input"
                          />
                        </div>
                      </div>

                      {/* Heat Annexure Table */}
                      {selectedHeatComponents.length > 0 && heatAnnexureQuantity > 0 && (
                        <div className="heat-annexure-table-container">
                          <table className="heat-annexure-table">
                            <thead>
                              <tr>
                                <th>Valve No.</th>
                                <th>Component</th>
                                <th>Heat Number</th>
                                <th>Material</th>
                                <th>TC No.</th>
                                <th>Vendor Name</th>
                                <th>Attachment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {heatAnnexureData.map((row, index) => (
                                <tr key={index}>
                                  <td className="valve-no-cell">
                                    {/* Valve No. is now a manual input field */}
                                    <input
                                      type="text"
                                      value={row.valveNo}
                                      onChange={(e) => {
                                        const newData = [...heatAnnexureData];
                                        newData[index].valveNo = e.target.value;
                                        setHeatAnnexureData(newData);
                                      }}
                                      placeholder="Enter Valve No."
                                      className="valve-no-input"
                                    />
                                  </td>
                                  <td>{row.component}</td>
                                  <td>
                                    <select
                                      value={row.heatNumber}
                                      onChange={(e) => handleHeatNumberChange(index, e.target.value)}
                                      className="heat-number-select"
                                    >
                                      <option value="">Select Heat No.</option>
                                      {getHeatNumbersForComponent(row.component).map(hn => (
                                        <option key={hn} value={hn}>{hn}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={row.material}
                                      onChange={(e) => {
                                        const newData = [...heatAnnexureData];
                                        newData[index].material = e.target.value;
                                        setHeatAnnexureData(newData);
                                      }}
                                      placeholder="Auto-filled"
                                      readOnly={!!row.material}
                                      className={row.material ? "has-value" : ""}
                                    />
                                  </td>
                                  <td>
                                    <select
                                      value={row.tcNo}
                                      onChange={(e) => handleTCNumberChange(index, e.target.value)}
                                      className="tc-number-select"
                                    >
                                      <option value="">Select TC No.</option>
                                      {getTCNumbersForComponentAndHeat(row.component, row.heatNumber).map(tcNo => (
                                        <option key={tcNo} value={tcNo}>{tcNo}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={row.vendorName}
                                      onChange={(e) => {
                                        const newData = [...heatAnnexureData];
                                        newData[index].vendorName = e.target.value;
                                        setHeatAnnexureData(newData);
                                      }}
                                      placeholder="Auto-filled"
                                      readOnly={!!row.vendorName}
                                      className={row.vendorName ? "has-value" : ""}
                                    />
                                  </td>
                                  <td className="attachment-cell">
                                    {row.attachmentId && row.attachmentFilename ? (
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openHeatAttachmentPreview(e, row.attachmentId);
                                        }}
                                        className="heat-attachment-link"
                                        title={`Preview: ${row.attachmentFilename}`}
                                        type="button"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', textDecoration: 'underline' }}
                                      >
                                        {/\.(jpg|jpeg|png)$/i.test(row.attachmentFilename) ? "🖼️" : "📄"}
                                        {" View"}
                                      </button>
                                    ) : (
                                      <span className="no-attachment-text">No attachment</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}

        {/* Assembly Section - Only show for automation when valve is selected */}
        {valveType === "automation" && hasValveInTCDS && (
          <div className="mtc-form-section">
            <div className="section-header">
              <h3>Assembly Consists of</h3>
            </div>

            {/* Details of Valve */}
            {getFormattedValveDetails() && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                  Details of Valve:
                </div>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: '#374151'
                  }}
                >
                  {getFormattedValveDetails()}
                </div>
              </div>
            )}

            {/* Accessory Details */}
            {(() => {
              const accessoryDetails = getAccessoryDetails();
              const accessoryLabels = {
                'actuator': 'Details of Actuator',
                'positioner': 'Details of Positioner',
                'air filter regulator': 'Details of Air Filter Regulator',
                'solenoid valve': 'Details of Solenoid Valve',
                'limit switch box': 'Details of Limit Switch Box',
                'airlock relay': 'Details of Airlock Relay',
                'pilot valve': 'Details of Pilot Valve / Volume Booster',
                'volume booster': 'Details of Pilot Valve / Volume Booster',
                'manual override': 'Details of Manual Override',
                'quick exhaust valve': 'Details of Quick Exhaust Valve',
                'junction box': 'Details of Junction Box',
                'flow control valve': 'Details of Flow Control Valve',
                'exhaust diverter valve': 'Details of Exhaust Diverter Valve',
                'fusible link': 'Details of Fusible Link Or Plug',
                'fusible plug': 'Details of Fusible Link Or Plug',
                'tubing': 'Details of Tubing & Fittings',
                'fittings': 'Details of Tubing & Fittings',
                'bracket': 'Details of Bracket/Coupling',
                'coupling': 'Details of Bracket/Coupling'
              };

              if (Object.keys(accessoryDetails).length === 0) return null;

              return (
                <>
                  {Object.keys(accessoryDetails).map(accType => {
                    const details = accessoryDetails[accType];
                    if (!details) return null;

                    const label = accessoryLabels[accType] || `Details of ${accType.toUpperCase()}`;

                    return (
                      <div key={accType} style={{ marginBottom: '20px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                          {label}:
                        </div>
                        <div
                          style={{
                            padding: '12px',
                            backgroundColor: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            lineHeight: '1.6',
                            color: '#374151'
                          }}
                        >
                          {details}
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}

        {valveType === 'automation' && (
        <div className="mtc-form-section">
          <div className="section-header">
            <h3>Legends</h3>
          </div>
          <div
            style={{
              padding: '12px',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '13px',
              lineHeight: '1.8',
              color: '#374151',
              fontFamily: 'monospace'
            }}
          >
            BV - Ball Valve; FB - Full Bore; RB - Reduced Bore; FS - Firesafe; RFS - Raised Face Serrated; RFF - Raised Face Flat; SOV - Solenoid Valve; LMS - Limit Switch Box; AF/R - Air Filter/Regulator; FC - Fail Close; FO - Fail Open; FST - Fail Stayput; LPI - Local Position Indicator; PPOS - Pneumatic Positioner; I/P - I To P Converter; PF - Power Fail; DA - Double Acting
          </div>
        </div>
        )}

        {/* Form Actions */}
        <div className="mtc-form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={handleBackToList}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-download"
            onClick={handleDownloadPdf}
            disabled={loading || isDownloading}
            style={{
              background: isDownloading ? '#6b7280' : '#4f46e5',
              color: 'white',
              marginRight: '10px',
              cursor: isDownloading ? 'not-allowed' : 'pointer',
              opacity: isDownloading ? 0.7 : 1,
              transition: 'background 0.2s, opacity 0.2s'
            }}
          >
            {isDownloading ? 'Generating PDF...' : 'Download PDF'}
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={loading}
          >
            Generate MTC Certificate
          </button>
        </div>
      </form>
    </div>
  );
}

// Form Field Component - Shows manual entry if no value from TCDS
function FormField({
  field, value, onChange, qty, tagNumbers, onTagNumberChange, dynamicOptions,
  ipcSrNoArray, valveBatchSrNoArray, tagNumbersFromTCDS,
  onIpcSrNoChange, onValveBatchSrNoChange
}) {
  const stringValue = String(value || "");
  const hasValue = stringValue.trim() !== "";
  const isDropdown = field.type === "select" || field.inputType === "dropdown" || field.inputType === "static";
  
  // Use dynamic options for unit, prepared_by_name, and approved_by if provided
  let options = masterDataOptions[field.id] || [];
  if (dynamicOptions) {
    if (field.id === "unit" && dynamicOptions.units?.length > 0) {
      options = dynamicOptions.units;
    }
    if ((field.id === "prepared_by_name" || field.id === "approved_by" || field.id === "prepared_by" || field.id === "qac_officer") && dynamicOptions.reviewers?.length > 0) {
      options = dynamicOptions.reviewers;
    }
    if (field.id === "declaration_of_compliance" && dynamicOptions.declarations?.length > 0) {
      options = dynamicOptions.declarations;
    }
    if (field.id === "testing_standard" && dynamicOptions.testingStandards?.length > 0) {
      options = dynamicOptions.testingStandards;
    }
  }
  
  if (isDropdown && options.length > 0) {
    return (
      <div className={`form-field ${hasValue ? "has-value" : "dropdown-entry"}`}>
        <label>
          {field.label}
        </label>
        <select
          name={field.id}
          value={stringValue}
          onChange={onChange}
          className={!hasValue ? "manual-input" : ""}
        >
          <option value="">Select {field.label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }
  

  // Special handling for ipc_sr_no - unified Valve Details section for all quantities
  if (field.id === "ipc_sr_no" && qty > 0) {
    return (
      <div className="form-field full-width" style={{ gridColumn: '1 / -1' }}>
        <label style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '15px', display: 'block' }}>
          Valve Details & Testing Pressure
        </label>
        <div style={{ marginTop: '15px' }}>
          {qty === 1 ? (
            // For qty = 1, show 3-column layout
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '15px'
            }}>
              {/* IPC SR. NO */}
              <div>
                <label style={{ fontSize: '0.875rem', color: '#333', marginBottom: '8px', display: 'block', fontWeight: '500' }}>
                  IPC SR. NO.
                </label>
                <input
                  type="text"
                  value={ipcSrNoArray?.[0] || ""}
                  onChange={(e) => onIpcSrNoChange(0, e.target.value)}
                  placeholder="Enter IPC SR. NO."
                  className="manual-input"
                  style={{ width: '100%', padding: '10px', fontSize: '0.95rem' }}
                />
              </div>

              {/* VALVE BATCH/SR.NO */}
              <div>
                <label style={{ fontSize: '0.875rem', color: '#333', marginBottom: '8px', display: 'block', fontWeight: '500' }}>
                  VALVE BATCH/SR.NO.
                </label>
                <input
                  type="text"
                  value={valveBatchSrNoArray?.[0] || ""}
                  onChange={(e) => onValveBatchSrNoChange(0, e.target.value)}
                  placeholder="Enter Valve Batch/SR.NO."
                  className="manual-input"
                  style={{ width: '100%', padding: '10px', fontSize: '0.95rem' }}
                />
              </div>

              {/* TAG NO (Read-only from TCDS) */}
              <div>
                <label style={{ fontSize: '0.875rem', color: '#333', marginBottom: '8px', display: 'block', fontWeight: '500' }}>
                  TAG NO.
                </label>
                <input
                  type="text"
                  value={tagNumbersFromTCDS?.[0] || ""}
                  readOnly
                  placeholder="—"
                  className="manual-input"
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '0.95rem',
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed',
                    color: '#666'
                  }}
                />
              </div>
            </div>
          ) : (
            // For qty > 1, show multiple rows with S.No column
            <div style={{ display: 'grid', gap: '12px' }}>
              {Array.from({ length: qty }, (_, index) => (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 1fr 1fr',
                  gap: '12px',
                  padding: '10px',
                  backgroundColor: '#fafafa',
                  border: '1px solid #e8e8e8',
                  borderRadius: '4px',
                  alignItems: 'end'
                }}>
                  {/* S.No */}
                  <div style={{ alignSelf: 'center', textAlign: 'center' }}>
                    <small style={{ color: '#555', fontWeight: '600', fontSize: '0.85rem' }}>
                      S.No {index + 1}
                    </small>
                  </div>

                  {/* IPC SR. NO */}
                  <div>
                    <label style={{ fontSize: '0.8rem', color: '#333', marginBottom: '5px', display: 'block', fontWeight: '500' }}>
                      IPC SR. NO.
                    </label>
                    <input
                      type="text"
                      value={ipcSrNoArray?.[index] || ""}
                      onChange={(e) => onIpcSrNoChange(index, e.target.value)}
                      placeholder="Enter"
                      className="manual-input"
                      style={{ width: '100%', padding: '8px', fontSize: '0.9rem' }}
                    />
                  </div>

                  {/* VALVE BATCH/SR.NO */}
                  <div>
                    <label style={{ fontSize: '0.8rem', color: '#333', marginBottom: '5px', display: 'block', fontWeight: '500' }}>
                      VALVE BATCH/SR.NO.
                    </label>
                    <input
                      type="text"
                      value={valveBatchSrNoArray?.[index] || ""}
                      onChange={(e) => onValveBatchSrNoChange(index, e.target.value)}
                      placeholder="Enter"
                      className="manual-input"
                      style={{ width: '100%', padding: '8px', fontSize: '0.9rem' }}
                    />
                  </div>

                  {/* TAG NO (Read-only from TCDS) */}
                  <div>
                    <label style={{ fontSize: '0.8rem', color: '#333', marginBottom: '5px', display: 'block', fontWeight: '500' }}>
                      TAG NO.
                    </label>
                    <input
                      type="text"
                      value={tagNumbersFromTCDS?.[index] || ""}
                      readOnly
                      placeholder="—"
                      className="manual-input"
                      style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '0.9rem',
                        backgroundColor: '#f5f5f5',
                        cursor: 'not-allowed',
                        color: '#666'
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // When qty > 0, skip rendering valve_batch_sr_no separately (it's in Valve Details section)
  if (field.id === "valve_batch_sr_no" && qty > 0) {
    return null;
  }

  // When qty > 0, skip rendering tag_no separately (it's in Valve Details section)
  if (field.id === "tag_no" && qty > 0) {
    return null;
  }
  
  // Date input needs special handling for display format
  if (field.type === "date") {
    return (
      <div className={`form-field ${hasValue ? "has-value" : `${field.inputType || "manual"}-entry`}`}>
        <label>
          {field.label}
        </label>
        <input
          type="text"
          name={field.id}
          value={hasValue ? formatDateDDMMYYYY(stringValue) : ""}
          onChange={onChange}
          placeholder={hasValue ? "" : "DD/MM/YYYY"}
          className={!hasValue ? "manual-input" : ""}
          readOnly={field.inputType === "system" || field.inputType === "tcds"}
        />
      </div>
    );
  }
  
  return (
    <div className={`form-field ${hasValue ? "has-value" : `${field.inputType || "manual"}-entry`}`}>
      <label>
        {field.label}
      </label>
      {field.type === "textarea" ? (
        <textarea
          name={field.id}
          value={stringValue}
          onChange={onChange}
          placeholder={hasValue ? "" : `Enter ${field.label.toLowerCase()}`}
          className={!hasValue ? "manual-input" : ""}
          readOnly={field.inputType === "system" || field.inputType === "tcds"}
          style={{ minHeight: '100px', fontFamily: 'inherit', padding: '10px', resize: 'vertical' }}
        />
      ) : (
        <input
          type={field.type === "number" ? "text" : field.type}
          name={field.id}
          value={stringValue}
          onChange={onChange}
          placeholder={hasValue ? "" : `Enter ${field.label.toLowerCase()}`}
          className={!hasValue ? "manual-input" : ""}
          readOnly={field.inputType === "system" || field.inputType === "tcds"}
        />
      )}
    </div>
  );
}

export default ValveMTCForm;
